import React, { useState, useEffect, useCallback, useMemo, useRef, memo, startTransition } from 'react';
import { instantCountdown } from '@/lib/eastern';
import { tagChipStyle } from '@/lib/tagColors';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getCaveGreeting } from '@/lib/greetings';
import { matchesSearch } from '@/lib/search';
import { getClientTipOfDay, getTipOfDay } from '@/lib/joy';
import api, { CLIENT_STATUSES, ADMIN_STATUSES, EDITOR_STATUSES, STATUS_COLORS, sanitizeProductLabel } from '@/lib/api';
import { BusinessHealthBanner } from '@/components/JoyFeatures';
import { GemProgress } from '@/components/JoyFeatures';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ADMIN_VIDEO_CATALOG, CUSTOM_PRODUCT_KEY, CULLING_KEY, NLE_OPTIONS, findCatalogProduct } from '@/lib/adminProductCatalog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import ProjectPanel from '@/components/ProjectDrawer';
import ReviewAskModal from '@/components/ReviewAskModal';
import CascadePreviewDialog from '@/components/CascadePreviewDialog';
import DeadlineCountdown from '@/components/DeadlineCountdown';
import MustHavesIntro from '@/components/MustHavesIntro';
import SongApprovalIntro from '@/components/SongApprovalIntro';
import EditorTimeSurvey from '@/components/EditorTimeSurvey';
import AnnouncementQueue from '@/components/announcements/AnnouncementQueue';
import { LinkStateChip, UploadStatusChip, DriveAccessChip, TransferMethodChip } from '@/components/LinkCheck';
import { AutoPrepChip } from '@/components/AutoPrepCard';
import { prepMarkShown } from '@/lib/autoprep';
import { SongWaitChip } from '@/components/SongApproval';
import KeepFootageLink from '@/components/KeepFootageLink';
import { boardNeedsLine, mediaTooltip } from '@/lib/keepLink';
import { keepUnchanged, unlessSame, sameRow, canonical } from '@/lib/rowEquality';
import { canRestoreFromTrash, TRASH_HELP_LEAD, TRASH_HELP_EMAIL } from '@/lib/trash';
// import MultipartGauge from '@/components/MultipartGauge'; // hidden 2026-07-15 per operator — component + backend + daily prune preserved for one-line restore
import { DndContext, DragOverlay, useSensor, useSensors, PointerSensor, closestCenter } from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmDialog';
import { stopOf, askWords, bulkWords, ASK_TITLE, ASK_YES, ASK_NO } from '@/lib/handMove';
import { statusInk, pillFill } from '@/lib/statusChart';
import { useIsLight } from '@/lib/useIsLight';
import { OPEN_PROJECT_KEY, OPEN_PROJECT_EVENT } from '@/lib/openProject';
import { Search, Plus, ChevronRight, ChevronDown, Check, X, GripVertical, Palette, ArrowRight, CalendarClock, Info, User, Film, Image, Star, Lightbulb, Send, Archive, Layers } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// ─── Column system: resizable + reorderable + persisted ─
const STORAGE_KEY = 'ug_col_prefs_v6';

function getDefaultColumns(role, isAdmin) {
  const cols = [];
  cols.push({ key: 'order_date', label: 'Ordered', defaultWidth: 80, minWidth: 55 });
  cols.push({ key: 'project', label: 'Project', defaultWidth: 180, minWidth: 100, expandable: true });
  cols.push({ key: 'product', label: 'Product', defaultWidth: 160, minWidth: 80 });
  cols.push({ key: 'client', label: role === 'client' ? 'Brand' : role === 'editor' ? 'Brand' : 'Client', defaultWidth: 150, minWidth: 100, expandable: true });
  // Event column: CLIENT ONLY (clients use it to gauge which weddings are
  // further out vs. need editing sooner). Removed for admin/staff/editor —
  // they get "Event Date:" surfaced in the drawer Notes tab + the "Event {Mon D}"
  // badge on qualifying rows instead.
  if (role === 'client') {
    cols.push({ key: 'event', label: 'Event', defaultWidth: 90, minWidth: 60 });
  }
  cols.push({ key: 'eta', label: isAdmin ? 'Editor Due' : role === 'editor' ? 'Due Date' : 'ETA', defaultWidth: 100, minWidth: 65, tooltip: role === 'client' ? 'Estimated delivery date. This is an estimate and not a guarantee — actual delivery may vary.' : isAdmin ? 'Editor due date. Hover the (i) to see the client-facing ETA (+3 days).' : null });
  if (isAdmin) {
    cols.push({ key: 'editor', label: 'Editor', defaultWidth: 55, minWidth: 50, center: true });
    cols.push({ key: 'pm', label: 'PM', defaultWidth: 55, minWidth: 50, center: true });
  }
  if (isAdmin || role === 'editor') {
    cols.push({ key: 'program', label: 'Program', defaultWidth: 100, minWidth: 55 });
  }
  if (role === 'editor') {
    cols.push({ key: 'payment', label: 'Payment', defaultWidth: 70, minWidth: 55, center: true });
  }
  cols.push(
    { key: 'media', label: 'Media', defaultWidth: 45, minWidth: 35, center: true },
    { key: 'music', label: 'Music', defaultWidth: 45, minWidth: 35, center: true },
    { key: 'notes', label: 'Notes', defaultWidth: 45, minWidth: 35, center: true },
    { key: 'status', label: 'Status', defaultWidth: 155, minWidth: 80 },
  );
  return cols;
}

function loadColPrefs(role) {
  try { const d = JSON.parse(localStorage.getItem(STORAGE_KEY + '_' + role)); if (d) return d; } catch {}
  return null;
}
function saveColPrefs(role, prefs) {
  try { localStorage.setItem(STORAGE_KEY + '_' + role, JSON.stringify(prefs)); } catch {}
}

function useColumns(role, isAdmin) {
  const defaults = getDefaultColumns(role, isAdmin);
  const [columns, setColumns] = useState(() => {
    const saved = loadColPrefs(role);
    if (saved?.order && saved?.widths) {
      const validKeys = new Set(defaults.map(c => c.key));
      const ordered = saved.order.filter(k => validKeys.has(k));
      defaults.forEach(c => { if (!ordered.includes(c.key)) ordered.push(c.key); });
      return ordered.map(k => {
        const def = defaults.find(c => c.key === k);
        return { ...def, width: saved.widths[k] || def.defaultWidth };
      });
    }
    return defaults.map(c => ({ ...c, width: c.defaultWidth }));
  });
  // Auto-fill: on first render, expand expandable columns to fill available width
  const didAutoFill = React.useRef(false);
  React.useEffect(() => {
    if (didAutoFill.current) return;
    const timer = setTimeout(() => {
      const el = document.querySelector('[data-testid="grouped-board"]');
      if (!el) return;
      const available = el.offsetWidth - 40;
      if (available < 600) return;
      setColumns(prev => {
        const total = prev.reduce((s, c) => s + c.width, 0) + 22;
        const extra = available - total;
        if (extra <= 10) return prev;
        const exp = prev.filter(c => c.expandable);
        if (!exp.length) return prev;
        const each = Math.floor(extra / exp.length);
        return prev.map(c => c.expandable ? { ...c, width: c.width + each } : c);
      });
      didAutoFill.current = true;
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const persist = (cols) => {
    const order = cols.map(c => c.key);
    const widths = {};
    cols.forEach(c => { widths[c.key] = c.width; });
    saveColPrefs(role, { order, widths });
  };

  const setWidth = (key, w) => {
    setColumns(prev => {
      const next = prev.map(c => c.key === key ? { ...c, width: Math.max(c.minWidth, w) } : c);
      persist(next);
      return next;
    });
  };

  const reorder = (fromIdx, toIdx) => {
    setColumns(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      persist(next);
      return next;
    });
  };

  const getWidth = (key) => columns.find(c => c.key === key)?.width || 100;

  return { columns, setWidth, reorder, getWidth };
}

// Resize handle — RIGHT edge of column, resizes THIS column
function ResizeEdge({ colKey, columns, setWidth }) {
  const ref = React.useRef(null);
  const col = columns.find(c => c.key === colKey);
  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = ref.current?.parentElement?.offsetWidth || col?.width || 100;
    let moved = false;
    const move = (ev) => { moved = true; setWidth(colKey, Math.max(col?.minWidth || 40, startW + (ev.clientX - startX))); };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Suppress the click event that fires after mouseup on the parent
      if (moved) {
        const suppress = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        ref.current?.parentElement?.addEventListener('click', suppress, { capture: true, once: true });
      }
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };
  return (
    <div ref={ref} onMouseDown={onMouseDown}
      className="absolute -right-[5px] top-0 bottom-0 w-[10px] cursor-col-resize z-20 flex items-center justify-center">
      <div className="w-[2px] h-3 rounded-full bg-slate-500/35 hover:bg-ug-gold/70 hover:h-5 transition-all" />
    </div>
  );
}

// Draggable + resizable column header
function DraggableColHeader({ col, idx, columns, setWidth, reorder, sortCol, sortDir, onSort }) {
  const [dragging, setDragging] = useState(false);
  const didDrag = React.useRef(false);
  const onDragStart = (e) => { didDrag.current = true; setDragging(true); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('col-idx', String(idx)); };
  const onDragEnd = () => { setDragging(false); setTimeout(() => { didDrag.current = false; }, 100); };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (e) => { e.preventDefault(); const f = parseInt(e.dataTransfer.getData('col-idx')); if (!isNaN(f) && f !== idx) reorder(f, idx); };
  const isSorted = sortCol === col.key;
  const arrow = isSorted ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  return (
    <div draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onDragOver={onDragOver} onDrop={onDrop}
      className={`relative shrink-0 px-1 cursor-grab active:cursor-grabbing select-none transition-opacity overflow-hidden ${col.center ? 'text-center' : 'text-left'} ${dragging ? 'opacity-40' : ''} ${idx === 0 ? 'pl-2' : ''}`}
      style={{ width: col.width }}
      onClick={(e) => { e.stopPropagation(); if (didDrag.current) return; if (onSort) onSort(col.key); }}>
      <span className={`cursor-pointer hover:text-ug-gold transition-colors ${isSorted ? 'text-ug-gold' : ''}`}>
        {col.tooltip ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1 cursor-help">
                  {col.label}{arrow} <Info className="w-3 h-3 text-slate-500" strokeWidth={1.5} />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-ug-raised border-ug-gold/20 text-zinc-100/80 text-[11px] max-w-[220px] font-body normal-case tracking-normal">
                {col.tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : <>{col.label}{arrow}</>}
      </span>
      {idx < columns.length - 1 && <ResizeEdge colKey={col.key} columns={columns} setWidth={setWidth} />}
    </div>
  );
}

// ─── Timeline cell: stacked ETA/DUE with inline date picker ─
function TimelineCell({ project, role, onSetDue }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [manualText, setManualText] = useState('');
  const committedRef = useRef(false);  // guards against a manual-blur + day-click double commit
  // Editor Due (operational, admin/editor only) shows post-FDC too so lateness
  // is visible at a glance — the cascade never mutates a project's OWN due date,
  // so the FDC+ value is the frozen due-at-transition. Client ETA stays blanked
  // post-FDC (stale editing estimate would mislead clients now in review/approval).
  const postDraft = (project.editor_status_id ?? 0) >= 2;
  const owesDraft = [0, 1].includes(project.editor_status_id);  // pre-FDC: editor still owes the first draft
  const editorDue = project.editor_due_date || '';
  const eta = postDraft ? '' : (project.eta || '');
  const now = new Date();

  // Late color fires ONLY while the editor still owes the first draft (status 0/1).
  // FDC+ / Revisions In Progress / Approved show the (frozen) due date in neutral
  // color — the first-draft deadline is historical. See OVERDUE_SIGNAL_AUDIT.md.
  const getDateColor = (dateStr) => {
    if (!dateStr) return 'text-slate-600';
    if (!owesDraft) return 'text-slate-400';
    const d = new Date(dateStr + 'T00:00:00');
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    if (diff < 0) return 'text-velvet-burgundy';
    if (diff <= 2) return 'text-ug-gold';
    return 'text-slate-400';
  };

  const fmt = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  // Client: only ETA — always neutral color (never show urgency to clients)
  if (role === 'client') {
    return (
      <div data-testid={`timeline-${project.id}`} className="flex items-center">
        {eta ? (
          <div className="font-sans text-[11px] font-medium text-zinc-400">
            <span className="text-[8px] text-zinc-500 uppercase tracking-wider">eta </span>{fmt(eta)}
          </div>
        ) : <span className="font-sans text-[10px] font-semibold text-slate-700">-</span>}
      </div>
    );
  }

  // Editor: pre-FDC shows a timezone-proof countdown to the Eastern deadline;
  // post-FDC the deadline is historical, so fall back to the frozen due date.
  if (role === 'editor') {
    const preFdc = (project.editor_status_id ?? 0) < 2;
    return (
      <div data-testid={`timeline-${project.id}`} className="flex items-center">
        {editorDue ? (
          preFdc ? (
            <DeadlineCountdown dueDate={editorDue} testId={`due-countdown-${project.id}`} />
          ) : (
            <div className={`font-sans text-[11px] font-medium ${getDateColor(editorDue)}`}>
              <span className="text-[8px] text-slate-500 uppercase tracking-wider">due </span>{fmt(editorDue)}
            </div>
          )
        ) : <span className="font-sans text-[10px] font-semibold text-slate-700">-</span>}
      </div>
    );
  }

  // Admin/Staff: react-day-picker in a Popover. Month/year navigation only changes
  // the VIEW — react-day-picker's onSelect fires SOLELY on a day-click — so browsing
  // the calendar can NEVER call onSetDue / trigger the cascade. A manual MM/DD/YYYY
  // field commits through the SAME funnel. The cascade flow (onSetDue ->
  // handleSetDueDate -> /cascade-preview -> dialog -> PUT) is unchanged; only WHAT
  // calls onSetDue changed (a deliberate selection/commit, not per-segment onChange).
  const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const ymdToMdy = (ymd) => { const [y, m, d] = ymd.split('-'); return `${m}/${d}/${y}`; };
  const parseManual = (s) => {
    const m = (s || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const mm = +m[1], dd = +m[2], yyyy = +m[3];
    const d = new Date(yyyy, mm - 1, dd);
    // reject overflow (02/31, 13/05, etc.) — JS Date normalizes, so round-trip-verify
    if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
    return toYmd(d);
  };
  // SINGLE commit funnel. Fires onSetDue ONLY on the first genuine, different change
  // (committedRef stops a manual-blur + day-click double fire). The same-value skip
  // here mirrors handleSetDueDate's own guard.
  const applyDate = (ymd) => {
    if (committedRef.current) return false;
    if (ymd && ymd !== editorDue) { committedRef.current = true; onSetDue(project.id, ymd); return true; }
    return false;
  };
  const tryCommitManual = () => { const ymd = parseManual(manualText); if (ymd && applyDate(ymd)) setPickerOpen(false); };
  const selectedDate = editorDue ? new Date(editorDue + 'T00:00:00') : undefined;
  return (
    <div data-testid={`timeline-${project.id}`} onClick={e => e.stopPropagation()}>
      <div className="flex items-center gap-1">
        <Popover open={pickerOpen} onOpenChange={(o) => { setPickerOpen(o); if (o) { committedRef.current = false; setManualText(editorDue ? ymdToMdy(editorDue) : ''); } }}>
          <PopoverTrigger asChild>
            <button data-testid={`set-due-${project.id}`}
              className={`font-sans text-[10px] font-semibold leading-snug hover:text-ug-gold transition-colors ${editorDue ? getDateColor(editorDue) : 'text-slate-700'}`}>
              {editorDue ? fmt(editorDue) : <span className="text-[8px] italic hover:text-ug-gold/70">+ set due</span>}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-ug-raised border-ug-border-strong" align="start" data-testid={`due-picker-${project.id}`}>
            <div className="p-2 border-b border-ug-border-subtle">
              <label className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">Type a date (MM/DD/YYYY)</label>
              <input type="text" inputMode="numeric" autoFocus placeholder="MM/DD/YYYY"
                data-testid={`due-date-input-${project.id}`}
                value={manualText}
                onChange={e => setManualText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); tryCommitManual(); } }}
                onBlur={tryCommitManual}
                className="font-mono text-xs bg-ug-surface border border-ug-gold/30 rounded px-2 py-1 h-8 w-40 text-zinc-100 focus:outline-none focus:border-ug-gold/60"
              />
            </div>
            <Calendar mode="single"
              selected={selectedDate}
              defaultMonth={selectedDate}
              onSelect={(d) => { if (d) { applyDate(toYmd(d)); setPickerOpen(false); } }}
              className="rounded-md"
            />
            {/* Ryan, 2026-10-01: a date could be changed but never removed. The server already clears it properly
                (the client ETA with it, auto-calculation unlocked, logged); this is the button. */}
            {editorDue && (
              <div className="p-2 border-t border-ug-border-subtle">
                <button type="button" data-testid={`due-clear-${project.id}`}
                  onClick={() => { if (committedRef.current) return; committedRef.current = true; onSetDue(project.id, ''); setPickerOpen(false); }}
                  className="tap-auto w-full text-[11px] text-slate-400 hover:text-zinc-100 py-1 rounded hover:bg-white/[0.04] transition-colors">
                  Clear due date
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        {eta && editorDue && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-[8px] text-ug-gold/40 cursor-help">i</span>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-ug-raised border-ug-gold/20 text-zinc-100/80 text-[11px] font-mono">
                Client sees ETA: {fmt(eta)}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}

// ─── Status helpers per role ────────────────────────────
function useStatusConfig(role) {
  // Memoised on role: the object is a prop of every memoised row, so a fresh literal
  // on each page render would re-render every row on every render and undo the memo.
  return useMemo(() => {
    if (role === 'editor') return { statuses: EDITOR_STATUSES, colors: STATUS_COLORS, track: 'editor', key: 'editor_status_id' };
    if (role === 'client') return { statuses: CLIENT_STATUSES, colors: STATUS_COLORS, track: null, key: 'client_status_id' };
    return { statuses: ADMIN_STATUSES, colors: STATUS_COLORS, track: 'admin', key: 'admin_status_id' };
  }, [role]);
}

// ─── StatusPill: inline dropdown to change status ───────
function StatusPill({ statusId, statuses, colors, track, projectId, onStatusChange, disabled }) {
  const color = colors[statusId] || colors[String(statusId)] || '#94A3B8';
  const label = statuses[statusId] || statuses[String(statusId)] || 'Unknown';
  const isPending = statusId === -1 || statusId === '-1';
  const isEditorNonEditable = track === 'editor' && ![0, 1, 2, 3, 4].includes(statusId);
  const pillClass = "font-sans text-[8px] font-medium uppercase tracking-wider rounded-md w-[140px] flex items-center justify-center shrink-0 leading-none";
  const pillStyle = (c) => ({ backgroundColor: pillFill(c), color: '#fff', height: '24px', maxHeight: '24px', minHeight: '24px' });

  if (disabled || !track || isPending || isEditorNonEditable) {
    const pill = (
      <span data-testid={`status-pill-${projectId}`} className={pillClass}
        style={pillStyle(color)}>{label}</span>
    );
    if (isPending) {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild><span>{pill}</span></TooltipTrigger>
            <TooltipContent side="bottom" className="bg-ug-raised border-ug-gold/20 text-zinc-100/80 text-[11px] font-body normal-case tracking-normal max-w-[220px]">
              This project is still being processed and is likely to be assigned to you, but is not officially ready to start yet.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return pill;
  }
  return (
    <Select value={String(statusId)} onValueChange={v => { v !== String(statusId) && onStatusChange(projectId, parseInt(v)); }}>
      <SelectTrigger data-testid={`status-pill-${projectId}`}
        className="w-[140px] border-0 p-0 focus:ring-0 focus:ring-offset-0 [&>svg]:hidden shrink-0 [&>span]:flex [&>span]:items-center [&>span]:justify-center [&>span]:w-full"
        style={{ height: '24px', minHeight: '24px', maxHeight: '24px' }}
        onClick={e => e.stopPropagation()}>
        <span className={pillClass} style={pillStyle(color)}>{label}</span>
      </SelectTrigger>
      <SelectContent className="bg-ug-raised border-ug-border-strong min-w-[200px]" onClick={e => e.stopPropagation()}>
        {Object.entries(statuses)
          .filter(([id]) => {
            // Editors can only set: Editing In Progress (1), First Draft Complete (2), Revisions In Progress (3), New Draft Complete (4)
            if (track === 'editor') return [0, 1, 2, 3, 4].includes(parseInt(id));
            return true; // Admin sees all
          })
          .map(([id, name]) => (
          <SelectItem key={id} value={id} className="text-zinc-100 text-xs">
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: colors[parseInt(id)] }} />
              {name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── The quiet mark (R209) ──────────────────────────────
// The order was auto-prepped. The server sends `prepped` to admin and editors alone (server.py's project list, and
// app/client_identity strips it from every client exit); this draws it for those two roles alone as well. No title,
// no label, no words (Ryan, 2026-10-30: "only we should know what the icon means").
function PrepMark({ project, role }) {
  if (!prepMarkShown(project, role)) return null;
  return <Layers aria-hidden="true" data-testid={`row-mark-${project.id}`} className="tap-auto w-3 h-3 shrink-0 text-oxidized-teal/60" />;
}

// ─── ReadyIcon ──────────────────────────────────────────
// The Media icon's words are lib/keepLink.mediaTooltip's: a client whose footage is a link of theirs is told about the
// link (we keep no copy of it), anyone else keeps the words below. The caller passes them as `tip`.
const READY_TOOLTIPS = {
  Music: { ready: 'Music has been received or selected', notReady: 'Music hasn\'t been provided yet — you can add it in the project' },
  Notes: { ready: 'Project notes and preferences are set', notReady: 'No project notes yet — you can add details in the project' },
};
function ReadyIcon({ ready, label, role, tip }) {
  const tips = READY_TOOLTIPS[label] || {};
  const tooltip = role === 'client'
    ? (tip || (ready ? tips.ready || `${label}: Ready` : tips.notReady || `${label}: Not ready yet`))
    : `${label}: ${ready ? 'Ready' : 'Not Ready'}`;
  return (
    <div title={tooltip} className={`ready-indicator ${ready ? 'ready' : 'not-ready'}`}>
      {ready ? <Check className="w-3 h-3" strokeWidth={2.5} /> : <X className="w-3 h-3" strokeWidth={2} />}
    </div>
  );
}

// ─── Draggable row ──────────────────────────────────────
/* Row checkbox for the grouped board. The board has no memoised rows, so any change to
   selectedIds re-renders every row (2.2-3.2 s for 535 rows in the dev build, ~1 s in
   production on a 513-row group, measured 2026-09-11). Two things make the click feel
   instant regardless: the box keeps its OWN checked state and flips it synchronously,
   and the board update is a React transition, so it can never block that flip. The
   prop resyncs the local state when the board catches up (clear, bulk actions, ranges).
   Shift is read on the capturing click so a range can be selected: the parent knows
   the visible order. */
const RowCheckbox = memo(function RowCheckbox({ id, checked, onToggle }) {
  const [local, setLocal] = useState(checked);
  useEffect(() => { setLocal(checked); }, [checked]);
  const shiftRef = useRef(false);
  return (
    <div className="w-[22px] shrink-0 flex items-center justify-center group"
      onClick={e => e.stopPropagation()}
      onMouseDown={e => { if (e.shiftKey) e.preventDefault(); }}   /* no text selection on shift-click */
      onClickCapture={e => { shiftRef.current = !!e.shiftKey; }}>
      <Checkbox checked={local}
        onCheckedChange={v => {
          const next = !!v; const shift = shiftRef.current; shiftRef.current = false;
          setLocal(next);
          startTransition(() => onToggle(id, next, shift));
        }}
        className="opacity-0 group-hover:opacity-100 data-[state=checked]:opacity-100 transition-opacity" />
    </div>
  );
});

function SelectableRow({ project, children, selectedIds, onToggle, isAdmin }) {
  const checked = selectedIds.has(project.id);
  return (
    <div className="flex items-center group">
      {isAdmin && (
        <div className="w-[22px] shrink-0 flex items-center justify-center">
          <Checkbox checked={checked} onCheckedChange={() => onToggle(project.id)}
            className="opacity-0 group-hover:opacity-100 data-[state=checked]:opacity-100 transition-opacity" style={{ width: '14px', height: '14px', minWidth: '14px' }} />
        </div>
      )}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

// ─── Droppable group ────────────────────────────────────
function DroppableGroup({ statusId, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: `group-${statusId}`, data: { statusId } });
  return (
    <div ref={setNodeRef} className={`transition-colors duration-200 ${isOver ? 'bg-ug-gold/[0.03] rounded-md' : ''}`}>
      {children}
    </div>
  );
}

// ─── Style profile completeness calculator ──────────────
function calcCompleteness(profile) {
  if (!profile || !profile.profile_type) return { pct: 0, filled: 0, total: 0 };
  let filled = 0, total = 0;
  const has = (v) => v && String(v).trim().length > 0;
  if (profile.profile_type === 'video' || profile.profile_type === 'both') {
    const v = profile.video || {};
    // Only mandatory fields count — camera, assets/LUTs, fonts are optional
    const checks = [
      has(v.story_telling), has(v.client_language),
      has(v.nle_software),
      v.reference_videos?.some(u => has(u)),
    ];
    total += checks.length;
    filled += checks.filter(Boolean).length;
  }
  if (profile.profile_type === 'photo' || profile.profile_type === 'both') {
    const p = profile.photo || {};
    const checks = [
      has(p.preset_filename) || p.reference_galleries?.some(u => has(u)),
      has(p.white_balance), has(p.exposure_contrast), has(p.skin_tone_bias),
      has(p.bw_usage), has(p.crop_straighten), has(p.burst_duplicates),
      has(p.editor_notes), has(p.culling_notes),
    ];
    total += checks.length;
    filled += checks.filter(Boolean).length;
  }
  return { pct: total > 0 ? Math.round((filled / total) * 100) : 0, filled, total };
}

// ─── Editing program helper ─────────────────────────────
// The row carries its own program (GET /projects, by the rule in backend app/project_style_profile.py), read as
// it is on every list. The board used to read a map fetched once when it opened, so a client who saved their
// Style Profile after that showed "-" until a reload.
function getEditingProgram(project) {
  // Mirrors the backend's is_photo_item guard (server.py:3503): a standalone
  // video culling order is labelled "Culling Only (<NLE>)" and contains "cull",
  // but it is NOT a photo product. Without this the Program column reported
  // Lightroom for video culling jobs.
  const isPhoto = (project.products || []).some(p => {
    const ll = p.toLowerCase();
    if (ll.includes('culling only') && !ll.includes('photo')) return false;
    return ll.includes('photo') || ll.includes('cull');
  });
  if (isPhoto) return 'Lightroom';
  return project.nle_software || '-';
}

// ─── Editor avatar picker (Monday.com style) ────────────
function getInitials2(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

// ONE definition of "does this project match the search box". Used by the visible
// list AND by the archived-match hint, so the count in "N archived also match"
// can never disagree with what appears when you click it.
const projectMatchesSearch = (p, search) => matchesSearch(search, [
  p.title, p.client_name, p.studio_name, p.brand_name, p.editor_name,
  p.event_date, p.payment_status, p.order_type, ...(p.products || []),
]);

// "2026-08-17" -> "Aug 18" — the day they are BACK, not the last day out.
// A label saying "out until the 17th" is ambiguous about whether the 17th is
// available; the first working day is the thing an admin is actually deciding
// against. Parsed as local midnight so it never slips a day.
const fmtOutDate = (ymd) => {
  try {
    const d = new Date(ymd + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ymd; }
};

function EditorPicker({ project, editors, editorWorkload, onAssign, recommendations }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const ed = editors.find(e => e.id === project.editor_id);
  const ew = editorWorkload[project.editor_id];
  // Resolve the project's NLE from the style profile (same source as the Program column)
  const projectNle = getEditingProgram(project);
  const hasNleFilter = projectNle && projectNle !== 'No preference' && projectNle !== '-' && projectNle !== 'Lightroom';
  // Map "Final Cut Pro X" (style profile value) to "Final Cut Pro" (editor tag value)
  const nleForMatch = projectNle === 'Final Cut Pro X' ? 'Final Cut Pro' : projectNle;
  // restricted editors cannot sign in: not offered (the one on the job still shows on its row)
  const assignable = editors.filter(e => !e.restricted);
  const nleMatched = hasNleFilter && !showAll
    ? assignable.filter(e => (e.nle_software || []).includes(nleForMatch))
    : assignable;
  const filtered = nleMatched.filter(e => matchesSearch(search, [e.name]))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  const initials = getInitials2(ed?.name);

  return (
    <div ref={ref} className="relative" onClick={e => e.stopPropagation()}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={() => setOpen(!open)} data-testid={`board-editor-picker-${project.id}`}
              style={ed ? { border: '2px solid rgba(123,107,138,0.5)' } : { border: '2px dashed rgba(100,116,139,0.3)' }}
              className={`tap-sm w-8 h-8 rounded-full flex items-center justify-center transition-all hover:ring-2 hover:ring-gold-leaf/30 overflow-hidden ${ed ? 'bg-ug-surface' : 'bg-ug-deepest'}`}>
              {ed ? (
                ed.profile_photo_b64 ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/photo-serve/${ed.id}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display=''; }} /> : null
              ) : null}
              {ed ? (
                <span className="font-sans text-[9px] font-semibold font-bold text-zinc-100/80" style={ed?.profile_photo_b64 ? {display:'none'} : {}}>{initials}</span>
              ) : (
                <User className="w-3.5 h-3.5 text-slate-600" strokeWidth={1.5} />
              )}
            </button>
          </TooltipTrigger>
          {ew && <TooltipContent side="bottom" className="bg-ug-raised border-ug-gold/20 text-zinc-100/80 text-[11px] p-3 max-w-[200px] font-body normal-case tracking-normal">
            <p className="font-medium text-zinc-100 mb-1">{ew.name}</p>
            <p className="text-[10px] text-slate-400 font-mono mb-1">{ew.email}</p>
            <p className="text-[10px] text-ug-gold">{ew.upcoming} upcoming project{ew.upcoming !== 1 ? 's' : ''}</p>
            {/* The already-assigned editor going away is the case you find out
                about latest — the dropdown is only opened when reassigning. */}
            {editors.find(e => e.id === project.editor_id)?.out_until && (
              <p className="text-[10px] text-orange-400 mt-1" data-testid={`avatar-out-until-${project.id}`}>
                Away — back {fmtOutDate(editors.find(e => e.id === project.editor_id).out_until)}
              </p>
            )}
          </TooltipContent>}
        </Tooltip>
      </TooltipProvider>

      {open && (
        <div className="absolute top-10 left-0 w-56 bg-ug-raised border border-ug-border-strong rounded-lg shadow-xl z-50 overflow-hidden" data-testid="editor-picker-dropdown">
          <div className="p-2 border-b border-ug-border">
            <div className="flex items-center gap-2 bg-ug-deepest/50 rounded px-2 py-1.5">
              <Search className="w-3.5 h-3.5 text-slate-500 tap-auto shrink-0" />
              <input value={search} onChange={e => setSearch(e.target.value)} autoFocus placeholder="Search editors..."
                className="bg-transparent text-sm text-zinc-100 outline-none w-full placeholder:text-slate-600" />
            </div>
          </div>
          {hasNleFilter && (
            <div className="px-2 py-1 border-b border-ug-border flex items-center justify-between">
              <span className="text-[8px] font-mono text-slate-500">{showAll ? 'All editors' : nleForMatch}</span>
              <button onClick={() => setShowAll(!showAll)} className="text-[8px] font-mono text-ug-gold/60 hover:text-ug-gold transition-colors">
                {showAll ? 'Filter by NLE' : 'Show all'}
              </button>
            </div>
          )}
          {/* Ryan, 2026-10-01: removing an editor was only possible by clicking the assigned editor again, which
              nothing said. Now it is a line of its own. */}
          {project.editor_id && (
            <button type="button" data-testid={`editor-picker-remove-${project.id}`}
              onClick={() => { onAssign(project.id, null); setOpen(false); setSearch(''); }}
              className="w-full flex items-center gap-2 px-3 py-2 border-b border-ug-border text-left text-[11px] text-slate-400 hover:text-zinc-100 hover:bg-white/[0.04] transition-colors">
              <X className="w-3.5 h-3.5 shrink-0" /> Remove {project.editor_name || 'editor'}
            </button>
          )}
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-center">
                <p className="text-[10px] text-slate-500">No editors tagged for {nleForMatch || 'this program'}</p>
              </div>
            )}
            {filtered.map(editor => {
              const isAssigned = editor.id === project.editor_id;
              const w = editorWorkload[editor.id];
              const outUntil = editor.out_until;
              const rec = (recommendations || []).find(r => r.id === editor.id);
              const isTopRec = rec && recommendations[0]?.id === editor.id;
              return (
                <button key={editor.id} onClick={() => {
                  if (isAssigned) { onAssign(project.id, null); } else { onAssign(project.id, editor.id); }
                  setOpen(false); setSearch('');
                }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-white/[0.04] transition-colors ${isAssigned ? 'bg-ug-gold/[0.06]' : ''}`}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 tap-auto bg-ug-surface border border-ug-border overflow-hidden" style={{ borderColor: isTopRec ? 'rgba(198,169,111,0.6)' : 'rgba(123,107,138,0.4)' }}>
                    {editor.profile_photo_b64 ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/photo-serve/${editor.id}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display=''; }} /> : null}
                    <span className="font-sans text-[8px] font-medium font-bold text-zinc-100/80" style={editor.profile_photo_b64 ? {display:'none'} : {}}>{getInitials2(editor.name)}</span>
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    {/* min-w-0 is load-bearing: without it `truncate` on the name
                        cannot shrink inside this flex row, so the shrink-0 badge
                        overflows and paints on top of the name. */}
                    <div className="flex items-center gap-1.5 min-w-0">
                      {/* opacity-50, NOT text-zinc-100/50: the latter forces a
                          near-WHITE colour at half alpha, which is legible in dark
                          theme and effectively invisible on the light one. Dim the
                          inherited colour instead so it works in both. */}
                      <span className={`text-[12px] truncate text-zinc-100 ${outUntil ? 'opacity-50' : ''}`}>{editor.name}</span>
                      {/* Availability sits on the NAME row, not down with the
                          metadata: it is a different KIND of fact from workload or
                          rating. Those help you choose between people who can do
                          the job; this one says they cannot right now. */}
                      {outUntil && (
                        <span data-testid={`picker-out-until-${editor.id}`}
                          className="tap-auto text-[7px] font-mono bg-orange-400/15 text-orange-400 px-1 py-0.5 rounded shrink-0 whitespace-nowrap">
                          BACK {fmtOutDate(outUntil)}
                        </span>
                      )}
                      {isTopRec && <span className="text-[7px] font-mono bg-ug-gold/20 text-ug-gold px-1 py-0.5 rounded shrink-0">TOP</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {w && <span className="text-[9px] text-slate-500 font-mono">{w.upcoming} projects</span>}
                      {rec && rec.combined_rating > 0 && <span className="text-[9px] text-ug-gold/70 font-mono">{rec.combined_rating}&#9733;</span>}
                      {rec && rec.nle_match && <span className="text-[7px] text-oxidized-teal font-mono">NLE &#10003;</span>}
                    </div>
                  </div>
                  {isAssigned && <Check className="w-3.5 h-3.5 text-ug-gold shrink-0 tap-auto" />}
                </button>
              );
            })}
            {filtered.length === 0 && <p className="text-center py-3 text-[11px] text-slate-600">No editors found</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function ClientTipOfDay() {
  const [expanded, setExpanded] = useState(false);
  const tip = getClientTipOfDay();
  return (
    <div className="mb-4" data-testid="client-tip-of-day">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-full bg-ug-gold/[0.04] border border-ug-gold/10 hover:bg-ug-gold/[0.06] transition-colors text-left">
        <Lightbulb className="w-3.5 h-3.5 text-ug-gold/60 shrink-0" />
        <span className="font-sans text-[10px] font-medium text-ug-gold/70 flex-1">Shooting Tip of the Day</span>
        <ChevronDown className={`w-3 h-3 text-ug-gold/40 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="mt-1 px-3 py-2.5 rounded-lg bg-ug-raised border border-ug-border">
          <p className="font-sans text-[11px] text-zinc-300 leading-relaxed">{tip}</p>
        </div>
      )}
    </div>
  );
}

function EditorTipOfDay() {
  const [expanded, setExpanded] = useState(false);
  const tip = getTipOfDay();
  return (
    <div className="mb-4" data-testid="editor-tip-of-day">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-full bg-ug-gold/[0.04] border border-ug-gold/10 hover:bg-ug-gold/[0.06] transition-colors text-left">
        <Lightbulb className="w-3.5 h-3.5 text-ug-gold/60 shrink-0" />
        <span className="font-sans text-[10px] font-medium text-ug-gold/70 flex-1">Editing Tip of the Day</span>
        <ChevronDown className={`w-3 h-3 text-ug-gold/40 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="mt-1 px-3 py-2.5 rounded-lg bg-ug-raised border border-ug-border">
          <p className="font-sans text-[11px] text-zinc-300 leading-relaxed">{tip}</p>
        </div>
      )}
    </div>
  );
}


// Chip labels for the Morning Brief board filter (keys unique per role)
const BRIEF_CARD_LABELS = {
  due_tomorrow: 'Due Tomorrow', overdue: 'Overdue', new_assignments: 'New Assignments',
  awaiting_feedback: 'Awaiting Client Feedback', revision_requests: 'Revisions Requested',
  unanswered_admin: 'Unread Messages', pending_admin: 'Under Admin Review',
  drafts_ready: 'Ready for Review', unanswered: 'Unread Messages', deletion_warnings: 'Download Soon',
  pending_review: 'Pending Review', unanswered_client: 'Client Chats', unanswered_editor: 'Editor Chats',
  mentions: 'Mentions',
  no_editor: 'No Editor', new_orders: 'New Orders', not_started: 'Not Started',
  awaiting_client: 'Awaiting Client', recently_approved: 'Needs Follow-Up', unverified_links: 'Link Never Verified',
  needs_attention: 'Needs Attention',
};

// An order the client placed whose payment did not go through. The server marks it for staff once the
// checkout window has passed (backend app/unpaid_orders.py); the desktop row and the mobile card both
// show it, with this one explanation.
const PAYMENT_FAILED_TIP = "The client placed this order but the payment didn't go through. Auto-advance holds it until it's paid; record an external payment if they paid another way.";
const PAYMENT_FAILED_CLS = 'tap-auto shrink-0 border border-ug-status-red/40 bg-ug-status-red/[0.08] text-ug-status-red rounded-full px-1.5 py-[1px] font-sans text-[8px] font-semibold uppercase tracking-wider whitespace-nowrap';

const NUDGE_COOLDOWN_MS = 24 * 3600000;
const nudgedAgo = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 3600000) return `${Math.max(1, Math.floor(ms / 60000))}m ago`;
  return `${Math.floor(ms / 3600000)}h ago`;
};
const inNudgeCooldown = (p) => p.last_nudged_at && (Date.now() - new Date(p.last_nudged_at).getTime()) < NUDGE_COOLDOWN_MS;
const inClientNudgeCooldown = (p) => p.last_client_nudged_at && (Date.now() - new Date(p.last_client_nudged_at).getTime()) < NUDGE_COOLDOWN_MS;

// ─── Main component ─────────────────────────────────────

/**
 * ONE BOARD ROW (desktop row + mobile card), memoised.
 *
 * Until 2026-09-12 this markup lived inline in DashboardPage's render, so every click
 * in the drawer, every keystroke in the search box and every 15-second poll re-rendered
 * every row on the board (410 in production): a drawer tab switch measured 923 ms with
 * 112 rows mounted and 114 ms with 4. Now a row re-renders only when one of its props
 * changes. THE RISK IS THE OPPOSITE ONE, a row that should update and does not, so:
 *
 *   * every value the row reads from the page is a prop (the list below was produced by
 *     scanning the body for page-scope identifiers, not by hand; that scan is what
 *     caught `cfg` and `archived`, which a hand-written list had missed);
 *   * `project` is replaced with a new object whenever its document changes (the poll
 *     keeps the old object ONLY when the whole document is byte-identical, see
 *     lib/rowEquality.js), so any field change re-renders the row;
 *   * `tick` advances once a minute so time-derived chips (hours left, overdue days)
 *     cannot freeze while the data is unchanged;
 *   * selection is passed as the booleans this row needs (`isSelected`, `isChecked`),
 *     not the Set, so selecting elsewhere does not redraw every row;
 *   * in development every render increments window.__rowRenders[project.id], which the
 *     staleness suite reads: a mutated field must bump it, an idle poll must not.
 */
const __lastRowProps = new Map();   // dev diagnostic only, see below
const ProjectRowItem = memo(function ProjectRowItem(props) {
  const { project, isSelected, user, isAdmin, columns, cfg, archived, briefFilterKey, editors, pms, editorPaymentMap, editorWorkload, editorRecs, awaitingDaysById, unpaidProjectIds, isChecked, uploadingProjectId, selectionAnchorRef, toggleSelection, openProject, setSelectedProject, setProjects, fetchProjects, bumpWorkload, handleStatusChange, handleSetDueDate, handleNudgeEditor, handleNudgeClient, handleSnoozeOverdue, handleDismissFollowUp, tick } = props;
  if (process.env.NODE_ENV !== 'production') {
    window.__rowRenders = window.__rowRenders || {};
    window.__rowRenders[project.id] = (window.__rowRenders[project.id] || 0) + 1;
    // and which props differed from the previous render, so a re-render names its cause
    const last = __lastRowProps.get(project.id);
    if (last) { window.__rowPropChanges = window.__rowPropChanges || {}; const w = window.__rowPropChanges; w[project.id] = [...new Set([...(w[project.id] || []), ...Object.keys(props).filter(k => props[k] !== last[k])])]; }   // union: StrictMode renders twice, the second pass sees no change
    __lastRowProps.set(project.id, props);
  }
  void tick;
  return (
    <div key={project.id}>
      {/* Desktop row — tight padding */}
      <div data-testid={`project-row-${project.id}`}
        onClick={e => {
          // Shift-click ANYWHERE on the row extends the range from the last
          // box you clicked. The box is 14px and hover-only; after scrolling
          // a hundred rows the natural second click lands on the row, and
          // opening the drawer there threw the range away.
          if (isAdmin && e.shiftKey && selectionAnchorRef.current) { toggleSelection(project.id, true, true); return; }
          openProject(project);
        }}
        onMouseDown={e => { if (e.shiftKey) e.preventDefault(); }}
        className={`hidden lg:flex items-center gap-0 pr-2 py-[7px] mx-1 rounded-lg cursor-pointer transition-all
          ${isSelected ? 'bg-ug-gold/[0.05]' : 'hover:bg-ug-surface/50'}
          ${project.editor_status_id === 1 ? 'editing-pulse' : ''}`}>
        {isAdmin && <RowCheckbox id={project.id} checked={isChecked} onToggle={toggleSelection} />}
        {columns.map(col => {
          const w = col.width;
          const s = { width: w };
          const cellCls = 'shrink-0 px-1 overflow-hidden';
          switch (col.key) {
            case 'order_date': return <div key={col.key} className="shrink-0 px-1 font-sans text-[10px] font-semibold text-slate-500" style={{ width: w }}>{project.created_at ? new Date(project.created_at).toLocaleDateString('en-US', {month:'short',day:'numeric'}) : '-'}</div>;
            case 'project': {
              const dueDate = project.editor_due_date || project.eta;
              const overdueDays = dueDate ? Math.floor((Date.now() - new Date(dueDate + 'T00:00:00').getTime()) / 86400000) : 0;
              return <div key={col.key} className={cellCls} style={s}>
                <div className="flex items-center gap-[12px]">
                  {user.role === 'client' && (
                    <button onClick={async (e) => { e.stopPropagation(); try { const r = await api.put(`/projects/${project.id}/star`); setProjects(prev => prev.map(p => p.id === project.id ? {...p, starred: r.data.starred} : p)); } catch {} }}
                      className="shrink-0" title={project.starred ? 'Remove from portfolio' : 'Add to portfolio'}>
                      <Star className="w-3.5 h-3.5 transition-colors" fill={project.starred ? '#D4C9A0' : 'none'} stroke={project.starred ? '#D4C9A0' : '#475569'} />
                    </button>
                  )}
                  {project.project_type === 'photo' ? <Image className="w-3 h-3 text-oxidized-teal shrink-0" /> : <Film className="w-3 h-3 text-ug-gold/50 shrink-0" />}
                  <span className="text-[13px] text-zinc-100 font-medium truncate">{project.title}</span>
                  <PrepMark project={project} role={user.role} />
                  {/* Only ever visible with the toggle on, where the board is a
                      mixed list — an archived row must not be mistaken for live work.
                      `my_archived` is the viewer's OWN archive (app/archive.py). */}
                  {project.my_archived && !archived && (
                    <span data-testid={`archived-badge-${project.id}`}
                      className="tap-auto shrink-0 text-[7px] font-mono uppercase tracking-wider bg-white/[0.06] text-slate-400 px-1 py-0.5 rounded">
                      Archived
                    </span>
                  )}
                  {uploadingProjectId === project.id && (
                    <div className="shrink-0 w-4 h-4 rounded-full border-2 border-ug-gold/30 border-t-ug-gold animate-spin" title="Uploading..." data-testid="upload-indicator" />
                  )}
                  {project.two_edits && (
                    <div className="shrink-0 self-center h-[18px] overflow-hidden flex items-start rounded-sm bg-ug-gold/15 border border-ug-gold/25">
                      <span className="text-ug-gold font-sans text-[9px] font-semibold whitespace-nowrap px-2 leading-[18px]" data-testid="double-edit-badge">
                        {project.two_edits_label || '2x'}
                      </span>
                    </div>
                  )}
                  {project.parent_project_id && (
                    <span data-testid="addon-badge" className="tap-auto shrink-0 self-center text-[7px] font-mono px-1.5 py-0.5 rounded text-oxidized-teal/70 bg-oxidized-teal/10 border border-oxidized-teal/20 whitespace-nowrap">ADD-ON</span>
                  )}
                  {isAdmin && unpaidProjectIds.has(project.id) && (
                    <span className="shrink-0 text-ug-gold tap-auto" title="Unpaid editor payment">💰</span>
                  )}
                  {overdueDays > 0 && project.client_status_id < 8 && [0, 1].includes(project.editor_status_id) && user.role !== 'client' && (
                    <span className="shrink-0 bg-ug-status-red/20 text-ug-status-red border border-ug-status-red/30 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full animate-pulse tap-auto">
                      {overdueDays}d overdue
                    </span>
                  )}
                </div>
                {/* flex-wrap, because this is a fixed-width column of shrink-0 chips: without it
                    one chip too many is laid out past the right edge, where it renders, answers
                    every query, and cannot be seen. That is how the hard-drive pill was invisible
                    on its first render (2026-09-19) while the DOM insisted it was there. */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {/* Wayfinding 1/3: a client scanning the list can see WHICH
                      project needs them without opening anything. Derived from
                      awaiting_client_files, so it clears itself when the request
                      resolves — same rule as the at-risk line.

                      ON THIS SECOND LINE, not up in the title row. Beside the
                      title it was `shrink-0` against a `truncate` title inside a
                      180px cell with `overflow-hidden`: the title collapsed to
                      0px (it wants 161px) and the chip STILL overhung the cell
                      by 11px, so the row rendered as a nameless project with a
                      badge reading "FILES REQUESTE". Measured 2026-07-30. This
                      line is the same answer as the mobile card's own row, for
                      the same reason: the title row has no space to give.

                      SOLID fill. `bg-ug-gold text-ug-deepest` is the app's
                      PRIMARY-ACTION pair (New Project and ~20 others) and is
                      theme-aware for free: index.css "PRIMARY ACTION: dark, not
                      gold" remaps `.light .bg-ug-gold` to the #1c1f25 slab with
                      #f2efe9 ink, on the reasoning that on paper a dark block
                      outranks gold. One class gives gold-on-dark in dark mode
                      and dark-on-paper in light — 8.4:1 and 14.4:1 label
                      contrast, louder than the status pill in both. The panel
                      slab in ClientFileRequest.js uses the same pair on purpose:
                      chip and panel are one language.

                      `bg-gold-leaf text-obsidian` looks equivalent and has more
                      precedents, including the SongApproval components this
                      feature is modelled on. It is a trap: NEITHER alias has a
                      `.light` rule, so it stays #c6a96f on paper at 1.9:1
                      against it. Verify gold by computed style, not by class
                      name. (A dead `.light .bg-ug-gold`→#b89a5a also sits at
                      index.css:439, overridden ~850 lines later inside a
                      comma-separated list — now caught by check 2b in
                      scripts/lint-light-css.js.) */}
                  {user.role === 'client' && project.awaiting_client_files && (
                    <span data-testid={`files-requested-${project.id}`}
                          className="tap-auto shrink-0 rounded-sm bg-ug-gold px-1.5 py-[1px] font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-deepest">
                      Files requested
                    </span>
                  )}
                  {project.current_version > 0 && <span className="font-sans text-[9px] font-semibold text-oxidized-teal">V{project.current_version}</span>}
                  {/* Internal tags, under the name, where the team actually looks (operator,
                      2026-09-19). EVERY tag the job carries, not only the watched ones: a tag you
                      cannot see on the board is a tag nobody acts on. The server sends `row_tags`
                      to admin and staff alone, already reduced to {label, color, watched} -- the
                      note and who added it stay in the drawer, where there is room to read them.
                      On THIS line for the reason written at length above: the title row has no
                      space to give and a chip beside a truncating title ate the title. */}
                  {/* First on the line: an unpaid order is the one thing here that needs chasing. */}
                  {project.payment_failed && (
                    <span data-testid={`row-payment-failed-${project.id}`} title={PAYMENT_FAILED_TIP} className={PAYMENT_FAILED_CLS}>
                      Payment failed
                    </span>
                  )}
                  {/* Cancelled, or money given back (app/refunds.py): the team's rows only, the server sends
                      neither the amount nor (for clients and editors) any refund field. */}
                  {project.cancelled ? (
                    <span data-testid={`row-cancelled-${project.id}`} data-tone="warning"
                      title="Cancelled. Its money is out of revenue; open it to see the refund."
                      className="tap-auto shrink-0 border border-amber-500/40 bg-amber-500/[0.07] text-amber-300 rounded-full px-1.5 py-[1px] font-sans text-[8px] font-semibold uppercase tracking-wider whitespace-nowrap">
                      Cancelled
                    </span>
                  ) : Number(project.refunded_amount || 0) > 0 && (
                    <span data-testid={`row-refunded-${project.id}`}
                      title="Part of this order was refunded. Revenue counts what the client kept."
                      className="tap-auto shrink-0 border border-ug-border text-slate-400 rounded-full px-1.5 py-[1px] font-sans text-[8px] font-semibold uppercase tracking-wider whitespace-nowrap">
                      Refunded ${Math.round(Number(project.refunded_amount))}
                    </span>
                  )}
                  {/* Derived, not typed (app/first_order.py). On the row because it is a thing to
                      notice while scanning, which is the whole reason the operator hand-made a tag
                      for it before this existed. */}
                  {project.first_order && (
                    <span data-testid={`row-first-order-${project.id}`}
                      title="Part of this client's first order with us."
                      className="tap-auto shrink-0 border border-oxidized-teal/40 bg-oxidized-teal/[0.07] text-oxidized-teal rounded-full px-1.5 py-[1px] font-sans text-[8px] font-semibold uppercase tracking-wider whitespace-nowrap">
                      First order
                    </span>
                  )}
                  {(project.row_tags || []).map(t => (
                    <span key={t.label} data-testid={`row-tag-${project.id}`} title={`Tagged ${t.label}`}
                      style={tagChipStyle(t.color)}
                      className="tap-auto shrink-0 border rounded-full px-1.5 py-[1px] font-sans text-[8px] font-semibold uppercase tracking-wider whitespace-nowrap">
                      {t.label}
                    </span>
                  ))}
                  {/* W1 (2026-05-27): `draft_approved` semantic is now "latest version released
                      to client". For per-version projects (`uses_per_version_gate=true`), the
                      backend keeps it synchronized with `versions[current_version-1].released_to_client`
                      at every release/upload site. So this badge fires correctly for V2-pending
                      on per-version projects (draft_approved flips back to false on V2 upload). */}
                  {project.draft_approved === false && [5, 7].includes(project.admin_status_id) && project.editor_status_id >= 2 && (user.role === 'admin' || user.role === 'staff') && (
                    <span className="bg-ug-gold/20 text-ug-gold border border-ug-gold/30 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full">Pending Review</span>
                  )}
                  {project.draft_approved === true && [5, 7].includes(project.admin_status_id) && (user.role === 'editor' || user.role === 'admin' || user.role === 'staff') && !project.revisions_requested && (
                    <span className="bg-oxidized-teal/20 text-oxidized-teal border border-oxidized-teal/30 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full">Awaiting Client Feedback</span>
                  )}
                  {project.revisions_requested && (user.role === 'editor' || user.role === 'admin' || user.role === 'staff') && (() => {
                    // Deadline the bonus scores against: request + 24h; the clock stops when the
                    // editor's upload STARTS (backend app/revision_response.py). Shown, not hidden.
                    const cd = instantCountdown(project.revision_due_at);
                    const paused = project.revision_paused;   // paused by an admin or PM (backend app/revision_clock.py)
                    return (
                      <span className="bg-ug-status-red/20 text-ug-status-red border border-ug-status-red/30 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap"
                        data-testid={`revision-countdown-${project.id}`}
                        title={paused ? `Revision clock paused${paused.by_name ? ` by ${paused.by_name}` : ''}: ${paused.reason}. Paused time does not count.`
                          : cd ? 'Revised draft due 24 hours after the request. The clock stops when your upload starts.' : undefined}>
                        Revision Requested{paused ? ' · Clock paused' : cd ? ` · ${cd.text.replace('Revision ', '')}` : ''}
                      </span>
                    );
                  })()}
                  {project.event_upcoming && isAdmin && (
                    <span data-testid={`event-upcoming-${project.id}`} className="tap-auto bg-ug-status-blue/20 text-ug-status-blue border border-ug-status-blue/30 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap" title="Event hasn't happened yet, so no footage is expected until after it. An empty link or no link at all is normal until then; this chip clears on its own once the date passes.">
                      {project.event_date ? `Event ${new Date(project.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Event Upcoming'}
                    </span>
                  )}
                  <LinkStateChip project={project} role={user.role} idSuffix={`-${project.id}`} />
                  <UploadStatusChip project={project} role={user.role} idSuffix={`-${project.id}`} />
                  <TransferMethodChip project={project} role={user.role} idSuffix={`-${project.id}`} />
                  <DriveAccessChip project={project} role={user.role} idSuffix={`-${project.id}`} />
                  <AutoPrepChip project={project} role={user.role} idSuffix={`-${project.id}`} />
                  {(isAdmin || user.role === 'editor') && <SongWaitChip project={project} idSuffix={`-${project.id}`} />}
                </div>
              </div>; }
            case 'product': {
              const pName = sanitizeProductLabel(project.product_name || (project.products || [])[0] || '-', user.role);
              const extraCount = (project.products || []).length - 1;
              return <div key={col.key} className="shrink-0 px-1 font-sans text-[10px] font-semibold text-slate-400 truncate flex items-center gap-1" style={{ width: w }} title={(project.products || []).map(p => sanitizeProductLabel(p, user.role)).join(', ')}>
                {pName}{extraCount > 0 && <span className="text-[8px] bg-white/[0.06] text-slate-500 px-1 py-0.5 rounded shrink-0">+{extraCount}</span>}
              </div>;
            }
            case 'client': return <div key={col.key} className={cellCls} style={s}>
              {user.role === 'editor' ? (
                <div className="text-[12px] text-zinc-100/70 truncate">{project.brand_name || project.studio_name || 'Unknown Brand'}</div>
              ) : user.role === 'client' ? (
                <div className="text-[12px] text-zinc-100/70 truncate">{project.brand_name || project.studio_name || project.client_name}</div>
              ) : (
                <>{/* the client's page, from the row (Ryan, 2026-10-01); the row itself still opens the drawer */}
                  {project.client_id ? (
                    <Link to={`/admin/users/${project.client_id}`} onClick={e => e.stopPropagation()}
                      data-testid={`row-client-link-${project.id}`} title={`Open ${project.client_name || 'the client'}'s page`}
                      className="tap-auto block text-[12px] text-zinc-100/70 truncate hover:text-oxidized-teal hover:underline underline-offset-2">
                      {project.brand_name || project.studio_name || project.client_name}
                    </Link>
                  ) : <div className="text-[12px] text-zinc-100/70 truncate">{project.brand_name || project.studio_name || project.client_name}</div>}
                  {(project.brand_name || project.studio_name) && <div className="text-[9px] text-slate-600 font-mono truncate">{project.client_name}</div>}</>
              )}
            </div>;
            case 'event': return <div key={col.key} className="shrink-0 px-1 font-sans text-[11px] font-medium text-slate-400" style={{ width: w }}>{project.event_date || '-'}</div>;
            case 'eta': return <div key={col.key} className="shrink-0 px-1" style={{ width: w }}><TimelineCell project={project} role={user.role} onSetDue={handleSetDueDate} /></div>;
            case 'editor': return <div key={col.key} className="shrink-0 px-1 flex justify-center" style={s} onClick={e => e.stopPropagation()}>
              <EditorPicker project={project} editors={editors} editorWorkload={editorWorkload} recommendations={editorRecs}
                onAssign={(pid, eid) => {
                  if (eid === null) {
                    api.put(`/projects/${pid}/unassign-editor`, {}).then(() => { toast.success('Editor removed. They have been told.'); fetchProjects(); bumpWorkload(null, project); }).catch(e => toast.error(e?.response?.data?.detail || 'Failed'));
                  } else {
                    api.put(`/projects/${pid}/assign-editor`, { editor_id: eid }).then(() => { toast.success('Editor assigned'); fetchProjects(); bumpWorkload(eid, project); }).catch(() => toast.error('Failed'));
                  }
                }} />
            </div>;
            case 'pm': return <div key={col.key} className="shrink-0 px-1 flex justify-center" style={s} onClick={e => e.stopPropagation()}>
              {(() => {
                const pm = pms.find(p => p.id === project.pm_id);
                const pmInitials = pm?.name ? pm.name.split(' ').map(n => n[0]).join('').toUpperCase() : '?';
                return (
                  <div className="relative">
                    <button onClick={(e) => { e.stopPropagation(); const el = e.currentTarget.nextElementSibling; if (el) el.classList.toggle('hidden'); }}
                      className={`tap-sm w-8 h-8 rounded-full flex items-center justify-center transition-all hover:ring-2 hover:ring-oxidized-teal/30 overflow-hidden ${pm ? 'bg-ug-surface' : 'bg-ug-deepest'}`}
                      style={pm ? { border: '2px solid rgba(62,123,121,0.5)' } : { border: '2px dashed rgba(100,116,139,0.3)' }}
                      title={pm?.name || 'Assign PM'}>
                      {pm ? (
                        pm.profile_photo_b64 ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/photo-serve/${pm.id}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display=''; }} /> : null
                      ) : null}
                      {pm ? <span className="font-sans text-[9px] font-semibold font-bold text-oxidized-teal/80" style={pm?.profile_photo_b64 ? {display:'none'} : {}}>{pmInitials}</span> : <User className="w-3.5 h-3.5 text-slate-600" strokeWidth={1.5} />}
                    </button>
                    <div className="hidden absolute top-10 right-0 w-48 bg-ug-raised border border-ug-border-strong rounded-lg shadow-xl z-50 overflow-hidden py-1">
                      <button onClick={() => { api.put(`/projects/${project.id}/assign-pm`, { pm_id: null }).then(() => { toast.success('PM removed'); fetchProjects(); }).catch(() => toast.error('Failed')); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.04] text-left ${!project.pm_id ? 'bg-ug-gold/[0.06]' : ''}`}>
                        <span className="text-[11px] text-slate-500">Unassigned</span>
                      </button>
                      {pms.map(p => (
                        <button key={p.id} onClick={() => { api.put(`/projects/${project.id}/assign-pm`, { pm_id: p.id }).then(() => { toast.success('PM assigned'); fetchProjects(); }).catch(() => toast.error('Failed')); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-white/[0.04] ${p.id === project.pm_id ? 'bg-ug-gold/[0.06]' : ''}`}>
                          <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-ug-surface overflow-hidden" style={{ border: '1.5px solid rgba(62,123,121,0.4)' }}>
                            {p.profile_photo_b64 ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/photo-serve/${p.id}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display=''; }} /> : null}
                            <span className="font-sans text-[8px] font-medium font-bold text-oxidized-teal/80" style={p.profile_photo_b64 ? {display:'none'} : {}}>{p.name.split(' ').map(n => n[0]).join('').toUpperCase()}</span>
                          </div>
                          <span className="text-[11px] text-zinc-100 truncate">{p.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>;
            case 'program': return <div key={col.key} data-testid={`row-program-${project.id}`} className="shrink-0 px-1 font-sans text-[10px] font-semibold text-slate-400 truncate" style={s} title={getEditingProgram(project)}>{getEditingProgram(project)}</div>;
            case 'payment': {
              // Payment only eligible at First Draft Complete (status >= 2)
              const editorStatus = project.editor_status_id ?? -1;
              if (editorStatus < 2) return <div key={col.key} className="shrink-0 flex justify-center" style={{ width: w }}><span className="font-sans text-[8px] font-medium text-slate-700">-</span></div>;
              const pay = editorPaymentMap[project.id];
              return <div key={col.key} className="shrink-0 flex justify-center" style={{ width: w }}>
                {!pay ? <span className="font-sans text-[8px] font-medium text-slate-600">-</span>
                  : pay.status === 'paid'
                    ? <span className="inline-flex flex-col items-center">
                        <span className="bg-oxidized-teal/20 text-oxidized-teal font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full">Paid</span>
                        {pay.paid_at && <span className="font-mono text-[7px] text-slate-500 mt-0.5">{new Date(pay.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                      </span>
                    : <span className="bg-ug-status-red/20 text-ug-status-red font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full">Unpaid</span>
                }
              </div>;
            }
            case 'media': {
              // MEDIA icon = footage physically in-hand, computed server-side via the
              // shared footage_received() helper (reliable signals, never the media_ready
              // cache). Desktop + mobile read the SAME flag so they can never disagree.
              return <div key={col.key} className={`shrink-0 flex justify-center ${user.role === 'client' ? 'cursor-pointer' : ''}`} style={{ width: w }} onClick={user.role === 'client' ? (e) => { e.stopPropagation(); setSelectedProject(project); } : undefined}><ReadyIcon ready={project.media_in_hand} label="Media" role={user.role} tip={mediaTooltip(project, project.media_in_hand)} /></div>;
            }
            case 'music': return <div key={col.key} className={`shrink-0 flex justify-center ${user.role === 'client' ? 'cursor-pointer' : ''}`} style={{ width: w }} onClick={user.role === 'client' ? (e) => { e.stopPropagation(); setSelectedProject(project); } : undefined}>{project.project_type === 'photo' ? <span className="text-[9px] text-slate-600 font-mono">—</span> : <ReadyIcon ready={project.music_ready} label="Music" role={user.role} />}</div>;
            case 'notes': return <div key={col.key} className={`shrink-0 flex justify-center ${user.role === 'client' ? 'cursor-pointer' : ''}`} style={{ width: w }} onClick={user.role === 'client' ? (e) => { e.stopPropagation(); setSelectedProject(project); } : undefined}><ReadyIcon ready={project.notes_ready} label="Notes" role={user.role} /></div>;
            case 'status': return <div key={col.key} className="shrink-0 flex items-center gap-1" style={s}>
              <StatusPill statusId={project[cfg.key] ?? 1} statuses={cfg.statuses} colors={cfg.colors} track={cfg.track} projectId={project.id} onStatusChange={handleStatusChange} disabled={user.role === 'client' || user.role === 'billing'} />
              {/* The green "Footage" pill used to sit here (waiting_on_footage). Retired 2026-09-11:
                  the link-state chip under the title now carries "Still uploading", the link's
                  verdict, and "Awaiting footage" when nothing has been sent at all. */}
              {isAdmin && project.admin_status_id === 1 && (
                <button title={project.processing ? 'Mark as not processing' : 'Mark as downloading/processing'}
                  onClick={(e) => { e.stopPropagation(); api.put(`/projects/${project.id}`, { processing: !project.processing }).then(() => fetchProjects()).catch(() => toast.error('Failed')); }}
                  className={`shrink-0 px-1.5 py-0.5 rounded-full font-mono text-[7px] font-semibold uppercase tracking-wider transition-all ${project.processing ? 'bg-amber-500/20 text-amber-400 animate-pulse' : 'bg-white/[0.04] text-slate-600 hover:text-amber-400 hover:bg-amber-500/10'}`}>
                  {project.processing ? 'DOWNLOADING' : '+ DL'}
                </button>
              )}
              {(isAdmin || user.role === 'editor') && project.editor_id && project.editor_due_date && project.editor_status_id === 0 && (() => {
                const sla = project.product_sla_days || 3;
                const dueDate = new Date(project.editor_due_date + 'T00:00:00Z');
                const shouldStartBy = new Date(dueDate.getTime() - sla * 86400000);
                const hoursPast = (Date.now() - shouldStartBy.getTime()) / 3600000;
                if (hoursPast < 0) return null;
                const isUrgent = hoursPast >= sla * 24 * 0.25;
                return <span className={`font-mono text-[7px] px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap ${isUrgent ? 'bg-ug-status-red/20 text-ug-status-red' : 'bg-ug-gold/15 text-ug-gold'}`}>{isUrgent ? 'LATE START' : 'NOT STARTED'}</span>;
              })()}
            </div>;
            default: return <div key={col.key} className="shrink-0" style={s}>-</div>;
          }
        })}
        {isAdmin && briefFilterKey === 'awaiting_client' && (() => {
          const d = awaitingDaysById.get(project.id);
          const label = d == null ? '\u2014' : d === 0 ? '<1d' : `${d}d`;
          const tone = d == null ? 'text-slate-600' : d < 3 ? 'text-slate-500' : d <= 7 ? 'text-slate-300' : 'text-ug-gold font-semibold';
          const canNudge = d != null && d >= 7 && project.client_id && (project.client_name || '').trim();
          return (
            <div className="shrink-0 ml-auto flex items-center gap-1">
              {canNudge && (inClientNudgeCooldown(project) ? (
                <span data-testid={`client-nudged-state-${project.id}`}
                  title={`Nudged ${new Date(project.last_client_nudged_at).toLocaleString()}`}
                  className="tap-auto flex items-center gap-1 px-2 py-1 text-[9px] font-medium text-oxidized-teal/70 whitespace-nowrap">
                  <Check className="w-3 h-3" /> Nudged {nudgedAgo(project.last_client_nudged_at)}
                </span>
              ) : (
                <button onClick={(e) => { e.stopPropagation(); handleNudgeClient(project); }}
                  data-testid={`nudge-client-${project.id}`}
                  title={`Post a friendly check-in to ${(project.client_name || '').trim().split(/\s+/)[0] || 'the client'} in this project's client chat`}
                  className="tap-auto flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium text-slate-500 hover:text-ug-gold hover:bg-ug-gold/10 transition-all">
                  <Send className="w-3 h-3" /> Nudge
                </button>
              ))}
              <div data-testid={`waiting-duration-${project.id}`}
                title={d == null ? 'No release anchor recorded' : `Waiting on client since the latest approve-and-send (${d === 0 ? 'today' : `${d} day${d === 1 ? '' : 's'} ago`})`}
                className={`tap-auto shrink-0 w-[60px] pr-1 text-right font-mono text-[10px] ${tone}`}>
                {label}
              </div>
            </div>
          );
        })()}
        {isAdmin && briefFilterKey === 'overdue' && (
          <div className="shrink-0 ml-auto flex items-center gap-1">
            {project.editor_id && project.editor_name && (
              inNudgeCooldown(project) ? (
                <span data-testid={`nudged-state-${project.id}`}
                  title={`Nudged ${new Date(project.last_nudged_at).toLocaleString()}`}
                  className="flex items-center gap-1 px-2 py-1 text-[9px] font-medium text-oxidized-teal/70 whitespace-nowrap">
                  <Check className="w-3 h-3" /> Nudged {nudgedAgo(project.last_nudged_at)}
                </span>
              ) : (
                <button onClick={(e) => { e.stopPropagation(); handleNudgeEditor(project); }}
                  data-testid={`nudge-editor-${project.id}`}
                  title={`Post a check-in to ${project.editor_name} in this project's internal chat`}
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium text-slate-500 hover:text-ug-gold hover:bg-ug-gold/10 transition-all">
                  <Send className="w-3 h-3" /> Nudge
                </button>
              )
            )}
            <button onClick={(e) => { e.stopPropagation(); handleSnoozeOverdue(project.id); }}
              data-testid={`snooze-overdue-${project.id}`}
              title="Snooze 1 day — hide from this brief until tomorrow morning (I've already chased the editor)"
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium text-slate-500 hover:text-ug-gold hover:bg-ug-gold/10 transition-all">
              <CalendarClock className="w-3 h-3" /> Snooze 1d
            </button>
          </div>
        )}
        {isAdmin && briefFilterKey === 'recently_approved' && (
          <button onClick={(e) => { e.stopPropagation(); handleDismissFollowUp(project.id); }}
            data-testid={`dismiss-follow-up-${project.id}`}
            title="Mark as followed up (e.g., already contacted by email or phone)"
            className="shrink-0 ml-auto flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium text-slate-500 hover:text-ug-gold hover:bg-ug-gold/10 transition-all">
            <X className="w-3 h-3" /> Dismiss
          </button>
        )}
      </div>
      {/* Mobile card */}
      <div data-testid={`project-card-${project.id}`}
        onClick={() => openProject(project)}
        className={`lg:hidden p-3 cursor-pointer transition-all border
          ${isSelected ? 'bg-ug-gold/[0.06] border-ug-gold/15' : 'border-white/[0.03] hover:bg-ug-surface/50'}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="text-[14px] text-zinc-100 font-medium truncate flex items-center gap-[12px]">
              <span className="truncate">{project.title}</span>
              <PrepMark project={project} role={user.role} />
              {project.two_edits && (
                <div className="shrink-0 self-center h-[18px] overflow-hidden flex items-start rounded-sm bg-ug-gold/15 border border-ug-gold/25">
                  <span className="text-ug-gold font-sans text-[9px] font-semibold whitespace-nowrap px-2 leading-[18px]">{project.two_edits_label || '2x'}</span>
                </div>
              )}
            </div>
            <div className="text-[12px] text-slate-400">{project.brand_name || project.studio_name || project.client_name}</div>
            {(project.brand_name || project.studio_name) && <div className="text-[9px] text-slate-600 font-mono">{project.client_name}</div>}
          </div>
          <StatusPill statusId={project[cfg.key] ?? 1} statuses={cfg.statuses} colors={cfg.colors} track={cfg.track} projectId={project.id} onStatusChange={handleStatusChange} disabled={user.role === 'client' || user.role === 'billing'} />
        </div>
        {/* Wayfinding 1/3, mobile copy of the chip in the `hidden lg:flex` row
            above. Same derivation, same styling — this card is a full duplicate
            render path, so the two are only consistent by hand. Change one,
            change both.

            Its OWN row, left-aligned, NOT inline beside the title like the
            desktop one — and this is load-bearing, not taste. The title span is
            `truncate` (white-space: nowrap) and an ancestor carries `min-w-max`,
            so that container sizes to max-content: the full untruncated title
            plus anything sitting inline next to it. Measured at 375px, same
            container, chip inline vs chip on its own row: 456px vs 347px. The
            375px viewport clips the difference.
            And that container wraps the WHOLE STATUS GROUP, so the cost is not
            paid by this card alone — one project's chip pushed the status pill
            of every sibling card off-screen, including cards with no chip at
            all. That is what makes it look like a pre-existing layout bug when
            it is not.
            So: nothing goes inline in that title row, and nothing goes
            right-aligned under the pill (same container, same effect). A short
            left-aligned row of its own costs the container nothing.

            Right-aligned so it sits under the status pill, which is safe ONLY
            because it is on its own row: a flex row's max-content is its
            content, so `justify-end` moves the chip within the container without
            widening it. Measured, same container: 347px both before and after
            right-aligning, vs 456px with the chip inline in the title row. It
            does inherit the pill's own exposure — both are flush to the content
            edge, so if a long title ever widens this group's container, chip and
            pill go off-screen together.

            Separately and genuinely pre-existing: long titles alone can still
            overflow by a few px under the same nowrap + min-w-max mechanism —
            see docs/KNOWN-ISSUES.md, mobile breakpoint entry. Untouched here. */}
        {user.role === 'client' && project.awaiting_client_files && (
          <div className="mb-2 flex justify-end">
            <span data-testid={`files-requested-mobile-${project.id}`}
                  className="tap-auto inline-block rounded-sm bg-ug-gold px-1.5 py-[1px] font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-deepest">
              Files requested
            </span>
          </div>
        )}
        {/* Mobile copy of the desktop row's Payment failed pill, on its own row for the reason above. */}
        {project.payment_failed && (
          <div className="mb-2 flex justify-end">
            <span data-testid={`row-payment-failed-mobile-${project.id}`} title={PAYMENT_FAILED_TIP} className={PAYMENT_FAILED_CLS}>
              Payment failed
            </span>
          </div>
        )}
        <div className="flex items-center gap-3 text-[11px] font-mono text-slate-500">
          {(() => {
            const mobileDate = user.role === 'client' ? project.eta : project.editor_due_date;
            const label = user.role === 'client' ? 'ETA' : 'Due';
            return mobileDate ? <span>{label}: {mobileDate}</span> : null;
          })()}
          {project.current_version > 0 && <span className="text-oxidized-teal">V{project.current_version}</span>}
          {project.event_upcoming && isAdmin && (
            <span data-testid={`event-upcoming-mobile-${project.id}`} className="tap-auto bg-ug-status-blue/20 text-ug-status-blue border border-ug-status-blue/30 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap" title="Event hasn't happened yet, so no footage is expected until after it. An empty link or no link at all is normal until then; this chip clears on its own once the date passes.">
              {project.event_date ? `Event ${new Date(project.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Event Upcoming'}
            </span>
          )}
          <LinkStateChip project={project} role={user.role} idSuffix={`-mobile-${project.id}`} />
          <UploadStatusChip project={project} role={user.role} idSuffix={`-mobile-${project.id}`} />
          <TransferMethodChip project={project} role={user.role} idSuffix={`-mobile-${project.id}`} />
          <DriveAccessChip project={project} role={user.role} idSuffix={`-mobile-${project.id}`} />
          <AutoPrepChip project={project} role={user.role} idSuffix={`-mobile-${project.id}`} />
          {(isAdmin || user.role === 'editor') && <SongWaitChip project={project} idSuffix={`-mobile-${project.id}`} />}
          <div className={`flex gap-1.5 ml-auto ${user.role === 'client' ? 'cursor-pointer' : ''}`}
            onClick={user.role === 'client' ? (e) => { e.stopPropagation(); setSelectedProject(project); } : undefined}>
            <ReadyIcon ready={project.media_in_hand} label="Media" role={user.role} tip={mediaTooltip(project, project.media_in_hand)} />
            <ReadyIcon ready={project.music_ready} label="Music" role={user.role} />
            <ReadyIcon ready={project.notes_ready} label="Notes" role={user.role} />
          </div>
        </div>
        {isAdmin && (briefFilterKey === 'overdue' || briefFilterKey === 'recently_approved') && (
          <div className="mt-2 flex justify-end gap-1">
            {briefFilterKey === 'overdue' ? (
              <>
                {project.editor_id && project.editor_name && (
                  inNudgeCooldown(project) ? (
                    <span data-testid={`nudged-state-mobile-${project.id}`}
                      className="flex items-center gap-1 px-2 py-1 text-[9px] font-medium text-oxidized-teal/70 whitespace-nowrap">
                      <Check className="w-3 h-3" /> Nudged {nudgedAgo(project.last_nudged_at)}
                    </span>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); handleNudgeEditor(project); }}
                      data-testid={`nudge-editor-mobile-${project.id}`}
                      className="flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium text-slate-500 hover:text-ug-gold bg-white/[0.03]">
                      <Send className="w-3 h-3" /> Nudge
                    </button>
                  )
                )}
                <button onClick={(e) => { e.stopPropagation(); handleSnoozeOverdue(project.id); }}
                  data-testid={`snooze-overdue-mobile-${project.id}`}
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium text-slate-500 hover:text-ug-gold bg-white/[0.03]">
                  <CalendarClock className="w-3 h-3" /> Snooze 1d
                </button>
              </>
            ) : (
              <button onClick={(e) => { e.stopPropagation(); handleDismissFollowUp(project.id); }}
                data-testid={`dismiss-follow-up-mobile-${project.id}`}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium text-slate-500 hover:text-ug-gold bg-white/[0.03]">
                <X className="w-3 h-3" /> Dismiss
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default function DashboardPage({ archived = false, trashed = false }) {
  // a status group's name is TEXT in its status hue; its legible step depends on the theme (lib/statusChart.js)
  const isLight = useIsLight();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // Always 'all' — unified table
  const [publicityFilter, setPublicityFilter] = useState('all'); // Filter by publicity preference
  const [statusPillFilter, setStatusPillFilter] = useState(null); // Single active admin status group filter (null=All)
  const [selectedProject, setSelectedProject] = useState(null);
  // The row handlers below read the CURRENT list and selection through refs rather
  // than closing over them: both are props of every memoised row, so a handler that
  // changed identity whenever one project changed re-rendered all of them.
  const projectsRef = useRef([]);
  const selectedProjectRef = useRef(null);
  projectsRef.current = projects;
  selectedProjectRef.current = selectedProject;
  const [pmOnDuty, setPmOnDuty] = useState(null); // { user_id, name } | null — shared with drawer via context-less fetch
  const [reviewAsk, setReviewAsk] = useState(null); // {eligible, project, approved_count} | null — Trustpilot prompt (client role only)
  const [drawerTab, setDrawerTab] = useState(null); // persists across project switches
  const [uploadingProjectId, setUploadingProjectId] = useState(null);
  const caveGreeting = useMemo(() => { const g = getCaveGreeting(user?.role); return g.subtitle; }, [user?.role]);
  const [panelDefaultTab, setPanelDefaultTab] = useState(null);
  const [panelExpandChat, setPanelExpandChat] = useState(null);

  // Fetch full project data when the drawer opens (the list view excludes heavy
  // fields like versions).
  useEffect(() => {
    if (selectedProject) {
      api.get(`/projects/${selectedProject.id}`).then(r => setSelectedProject(r.data)).catch(() => {});
    }
  }, [selectedProject?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectionAnchorRef = useRef(null);   // last row whose box was clicked: the other end of a shift-click range
  const [archiveConfirm, setArchiveConfirm] = useState(false); // two-step confirm for bulk archive
  // Reset the archive confirm whenever the selection changes so a stale confirm can't apply to a different set
  useEffect(() => { setArchiveConfirm(false); }, [selectedIds]);
  const [collapsed, setCollapsed] = useState({});
  const [showNewProject, setShowNewProject] = useState(false);
  const [sortCol, setSortCol] = useState('order_date'); // default sort column
  const [sortDir, setSortDir] = useState('asc'); // 'asc' = oldest first (default)
  const [newTitle, setNewTitle] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [newClientId, setNewClientId] = useState('');
  const [newProductId, setNewProductId] = useState('');       // free-text label, used only when Custom is picked
  const [newProductKey, setNewProductKey] = useState('');     // catalog key, or CUSTOM_PRODUCT_KEY
  const [newCullingNle, setNewCullingNle] = useState('');     // NLE for Culling Only (part of its label)
  const [newAmount, setNewAmount] = useState('');             // operator-entered price; prefilled from catalog
  const [newIsPaid, setNewIsPaid] = useState(true);           // payments happen outside the system
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [clientBrands, setClientBrands] = useState([]);
  const [myBrands, setMyBrands] = useState([]);
  const [activeId, setActiveId] = useState(null);
  // Due-date cascade preview state — populated by handleSetDueDate before
  // the PUT fires; cleared on cancel/confirm. The pendingDateEdit holds
  // everything needed to either commit or revert when the modal resolves.
  const [cascadePreview, setCascadePreview] = useState(null);  // SLA re-derive plan {anchor, moves[], new_date} or {error:true}
  const [pendingDateEdit, setPendingDateEdit] = useState(null); // {projectId, projectTitle, newDate, oldProjectsSnapshot}
  const [cascadeSubmitting, setCascadeSubmitting] = useState(false);
  const [actionItems, setActionItems] = useState(null);
  const [briefFilterKey, setBriefFilterKey] = useState(null); // active Morning Brief board filter (session-only)
  const briefCollapseSnapshot = useRef(null); // pre-filter collapse state, restored on clear
  const [briefCollapsed, setBriefCollapsed] = useState(false);
  const [businessHealth, setBusinessHealth] = useState(null);
  const [editorPaymentMap, setEditorPaymentMap] = useState({});
  const [styleProfile, setStyleProfile] = useState(null);
  const [editors, setEditors] = useState([]);
  const [editorRecs, setEditorRecs] = useState([]);
  const [pms, setPms] = useState([]);
  // ── Archived on the main board ──
  // Archiving exists to keep the board fast, but it also made past work
  // un-findable: searching a former client returned nothing and looked like they
  // had never ordered. This is the opt-in that widens the board instead of
  // sending you to a separate page. Persisted, because "I am looking through
  // history today" is a mode, not a per-search decision.
  const [includeArchived, setIncludeArchived] = useState(
    () => localStorage.getItem('ug_include_archived') === '1');
  useEffect(() => {
    localStorage.setItem('ug_include_archived', includeArchived ? '1' : '0');
  }, [includeArchived]);
  // Archived projects fetched ONCE, lazily, only to answer "would including them
  // help?" — never on the initial board load, which is the load we are protecting.
  const [archivedPeek, setArchivedPeek] = useState(null);
  const [editorWorkload, setEditorWorkload] = useState({});
  const [unpaidProjectIds, setUnpaidProjectIds] = useState(new Set());
  const navigate = useNavigate();

  const cfg = useStatusConfig(user?.role);
  const isAdmin = user?.role === 'admin' || user?.role === 'staff';
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const { columns, setWidth, reorder, getWidth } = useColumns(user?.role, isAdmin);

  const fetchProjects = useCallback(async () => {
    try {
      // include_archived widens the board to both lists. The default board still
      // asks for archived:false, so the reason archiving exists — keeping this
      // query small on a busy account — is untouched unless you opt in.
      const params = trashed ? { deleted: true }
        : (!archived && includeArchived) ? { include_archived: true }
        : { archived };
      const res = await api.get('/projects', { params });
      // Preserve a newer LOCAL nudge stamp: an in-flight poll response fetched
      // before the stamp PUT landed must not flip a just-nudged row back to
      // nudgeable (accidental double-nudge window). Same-format ISO-Z strings
      // compare lexicographically.
      setProjects(prev => {
        if (!prev?.length) return res.data;
        const stamps = {};
        const cstamps = {};
        for (const p of prev) {
          if (p.last_nudged_at) stamps[p.id] = p.last_nudged_at;
          if (p.last_client_nudged_at) cstamps[p.id] = p.last_client_nudged_at;
        }
        const merged = res.data.map(p => {
          const local = stamps[p.id];
          const clocal = cstamps[p.id];
          let out = p;
          if (local && (!p.last_nudged_at || local > p.last_nudged_at)) out = { ...out, last_nudged_at: local };
          if (clocal && (!p.last_client_nudged_at || clocal > p.last_client_nudged_at)) out = { ...out, last_client_nudged_at: clocal };
          return out;
        });
        // Keep the previous object for every row whose document is byte-identical, so
        // the memoised rows skip the poll; a changed document is a new object and redraws.
        return keepUnchanged(prev, merged);
      });
      // Deep link from email: ?open={project_id}&chat={system}
      const openId = searchParams.get('open');
      const chatSystem = searchParams.get('chat');
      if (openId) {
        const openIt = (proj) => {
          setSelectedProject(proj);
          // and the project's row on the board behind it, so closing the drawer leaves you at the project rather than
          // at the top of the list (operator, 2026-10-05). A project the board is not showing (archived) has no row.
          setTimeout(() => document.querySelector(`[data-testid="project-row-${proj.id}"]`)?.scrollIntoView({ block: 'center' }), 400);
          if (chatSystem) setPanelExpandChat(chatSystem);
          const tabParam = searchParams.get('tab');
          if (tabParam) setDrawerTab(tabParam);
        };
        const proj = (res.data || []).find(p => p.id === openId);
        if (proj) {
          openIt(proj);
        } else {
          // NOT IN THE FETCHED LIST — fetch it by id instead of doing nothing.
          // A deep link resolved only against the loaded board silently failed for
          // anything the board wasn't showing: archived projects above all, which
          // is exactly what an All Chats row or a notification email points at
          // once archived threads are listed. You landed on the dashboard and
          // nothing opened, with no error to explain it.
          // GET /projects/{id} is ownership-gated but has no archived filter, so
          // this opens the drawer without touching the board's include-archived
          // preference — a link should never silently rewrite a saved setting.
          api.get(`/projects/${openId}`)
            .then(r => { if (r.data) openIt(r.data); })
            .catch(() => {});
        }
        setSearchParams({}, { replace: true }); // clear params so refresh doesn't re-trigger
      }
      // Check if there's a project to auto-open from sessionStorage
      const openReq = sessionStorage.getItem(OPEN_PROJECT_KEY);
      if (openReq && !openId) {
        sessionStorage.removeItem(OPEN_PROJECT_KEY);
        try {
          const { projectId, tab } = JSON.parse(openReq);
          const proj = (res.data || []).find(p => p.id === projectId);
          if (proj) { setSelectedProject(proj); if (tab) setDrawerTab(tab); }
        } catch {}
      }
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [archived, trashed, includeArchived, searchParams, setSearchParams]);

  useEffect(() => { fetchProjects(); const iv = setInterval(fetchProjects, 15000); return () => clearInterval(iv); }, [fetchProjects]);
  // the chat pop-up asks for a project while the board is on screen: open it now, without reloading the page
  useEffect(() => {
    const now = () => fetchProjects();
    window.addEventListener(OPEN_PROJECT_EVENT, now);
    return () => window.removeEventListener(OPEN_PROJECT_EVENT, now);
  }, [fetchProjects]);

  // "PM on duty" indicator — fetched once at dashboard mount. Server-side
  // single source of truth via db.settings.pm_on_duty (admin-only writes).
  // Value shape: {user_id, name} when set; empty string when never set → render nothing.
  // Refresh cadence = page load; no polling needed per operator spec.
  useEffect(() => {
    api.get('/settings/pm_on_duty')
      .then(r => {
        const v = r.data?.value;
        if (v && typeof v === 'object' && v.user_id) setPmOnDuty(v);
        else setPmOnDuty(null);
      })
      .catch(() => setPmOnDuty(null));
  }, []);

  // Trustpilot review-ask (Layer 1 — in-portal peak-emotion prompt).
  // Server enforces cohort filter + 90d cooldown + permanent-skip — the
  // frontend cannot force a prompt outside the qualifying window. Fetch fires
  // once on mount; modal shows if server returns eligible=true.
  const [mustHavesDismissed, setMustHavesDismissed] = useState(false);
  useEffect(() => {
    if (user.role !== 'client') return; // Non-client roles skip the fetch
    api.get('/review-ask/mine')
      .then(r => {
        if (r.data?.eligible) setReviewAsk(r.data);
      })
      .catch(() => { /* fail-silent; no modal on network error */ });
  }, [user.role]);

  // Modal stacking guard: Must-Haves intro (z-[200], per-session) must dismiss
  // before the review-ask modal (z-[60]) becomes visible. Poll every 600ms
  // until mh-intro-modal is absent from the DOM, then flip mustHavesDismissed.
  // Cleared automatically if user isn't client.
  useEffect(() => {
    if (user.role !== 'client') { setMustHavesDismissed(true); return; }
    if (mustHavesDismissed) return;
    const iv = setInterval(() => {
      if (!document.querySelector('[data-testid="mh-intro-modal"]')) {
        setMustHavesDismissed(true);
        clearInterval(iv);
      }
    }, 600);
    // Safety timeout — after 30s just release the guard so the review-ask
    // modal isn't silently stuck if Must-Haves' DOM node is renamed.
    const timeout = setTimeout(() => { setMustHavesDismissed(true); clearInterval(iv); }, 30000);
    return () => { clearInterval(iv); clearTimeout(timeout); };
  }, [user.role, mustHavesDismissed]);

  const fetchActionItems = useCallback(() => {
    if (user?.role === 'admin' || user?.role === 'staff') {
      api.get('/admin/action-items').then(r => setActionItems(prev => unlessSame(prev, r.data))).catch(() => {});
    } else if (user?.role === 'editor') {
      api.get('/editor/action-items').then(r => setActionItems(prev => unlessSame(prev, r.data))).catch(() => {});
      api.get('/editor-payments/my-status').then(r => setEditorPaymentMap(prev => unlessSame(prev, r.data))).catch(() => {});
    } else if (user?.role === 'client') {
      api.get('/client/action-items').then(r => setActionItems(prev => unlessSame(prev, r.data))).catch(() => {});
    }
  }, [user?.role]);

  // W2 (2026-05-27): manual dismiss for "Needs Follow-Up" tile rows.
  // Optimistic local removal; revert via authoritative re-fetch on failure.
  // See backend: POST /api/projects/{id}/dismiss-follow-up.
  const handleDismissFollowUp = useCallback(async (projectId) => {
    setActionItems(prev => prev ? {
      ...prev,
      recently_approved: (prev.recently_approved || []).filter(it => it.id !== projectId),
    } : prev);
    try {
      await api.post(`/projects/${projectId}/dismiss-follow-up`);
    } catch (err) {
      fetchActionItems();
      toast.error('Failed to dismiss — please try again');
    }
  }, [fetchActionItems]);

  // Nudge: posts the templated check-in through the EXACT manual internal-chat
  // path (same endpoint/author/mention semantics as ChatThread). Cooldown stamp
  // last_nudged_at is separate metadata via the generic project PUT.
  const handleNudgeEditor = useCallback(async (project) => {
    if (!project.editor_id || !project.editor_name) return;
    const dueFmt = project.editor_due_date
      ? new Date(project.editor_due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : 'its due date';
    const bodyText = `Hey @${project.editor_name}, just checking in for an update, this was due on ${dueFmt}`;
    const prevStamp = project.last_nudged_at || null;
    const now = new Date().toISOString();
    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, last_nudged_at: now } : p));
    try {
      await api.post(`/chat/internal/${project.id}/messages`, { body: bodyText, mentions: [project.editor_id] });
      api.put(`/projects/${project.id}`, { last_nudged_at: now }).catch(() => {});
      toast.success(`Nudge sent to ${project.editor_name}`);
    } catch {
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, last_nudged_at: prevStamp } : p));
      toast.error('Failed to send nudge — please try again');
    }
  }, []);

  // "Nudge client" (awaiting-client filter): posts the variant message through the
  // EXACT manual client-chat path (same endpoint/payload ChatThread uses — plain
  // text body, no mentions), authored by the clicking admin. Variant matrix:
  // draft type (current_version>=2 ⇒ revision; legacy pre-R1 photo cv=0 ⇒ first
  // draft) × cycle history (last_client_nudged_at AFTER draft_approved_at ⇒
  // follow-up; every approve-and-send re-stamps the anchor ⇒ per-cycle reset).
  const handleNudgeClient = useCallback(async (project) => {
    if (!project.client_id || !(project.client_name || '').trim()) return;
    const first = project.client_name.trim().split(/\s+/)[0];
    const isRevision = (project.current_version || 0) >= 2;
    const isFollowUp = !!(project.last_client_nudged_at && project.draft_approved_at &&
      Date.parse(project.last_client_nudged_at) > Date.parse(project.draft_approved_at));
    const bodyText = isRevision
      ? (isFollowUp
        ? `Hi ${first}! Following up — we want to make sure the latest draft is everything you hoped for. If there's anything else you'd like adjusted, we're happy to help. Otherwise, clicking Approve will close things out and unlock your final files!`
        : `Hi ${first}! Just checking in — have you had a chance to review the latest draft? If you have any other requests, just let us know. If everything looks good, you can click Approve on your end to close it out and access your final files!`)
      : (isFollowUp
        ? `Hi ${first}! Following up — just wanted to make sure the first draft didn't slip through the cracks. Let us know your thoughts whenever you get a chance!`
        : `Hi ${first}! Just checking in — have you had a chance to check out the first draft? Would love to hear your thoughts whenever you're ready!`);
    const prevStamp = project.last_client_nudged_at || null;
    const now = new Date().toISOString();
    setProjects(prev => prev.map(p => p.id === project.id ? { ...p, last_client_nudged_at: now } : p));
    try {
      await api.post(`/chat/client/${project.id}/messages`, { body: bodyText });
      api.put(`/projects/${project.id}`, { last_client_nudged_at: now }).catch(() => {});
      toast.success(`Nudge sent to ${first}`);
    } catch {
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, last_client_nudged_at: prevStamp } : p));
      toast.error('Failed to send nudge — please try again');
    }
  }, []);

  const handleSnoozeOverdue = useCallback(async (projectId) => {
    // Optimistically drop it from the brief's overdue list + total badge ("handled
    // for today"). Server stamps overdue_snoozed_until = next ET morning; it
    // reappears at the next brief if still overdue. View-only — nothing real moves.
    setActionItems(prev => prev ? {
      ...prev,
      overdue: (prev.overdue || []).filter(it => it.id !== projectId),
      total_items: Math.max(0, (prev.total_items || 0) - 1),
    } : prev);
    try {
      await api.post(`/projects/${projectId}/snooze-overdue`);
      toast.success('Snoozed until tomorrow morning');
    } catch (err) {
      fetchActionItems();
      toast.error('Failed to snooze — please try again');
    }
  }, [fetchActionItems]);

  useEffect(() => {
    fetchActionItems();
    const iv = setInterval(() => {
      fetchActionItems();
      // Also refresh style profile for clients to keep completeness bar in sync
      if (user?.role === 'client') {
        api.get('/style-profile').then(r => setStyleProfile(r.data)).catch(() => {});
      }
    }, 15000);
    return () => clearInterval(iv);
  }, [fetchActionItems, user?.role]);

  // Fetch style profile for client completeness bar
  useEffect(() => {
    if (user?.role === 'client') {
      api.get('/style-profile').then(r => setStyleProfile(r.data)).catch(() => {});
      api.get(`/users/${user.id}/brands`).then(r => setMyBrands(r.data || [])).catch(() => setMyBrands([user.company_name].filter(Boolean)));
    }
    if (user?.role === 'admin' || user?.role === 'staff') {
      api.get('/users/editors').then(r => setEditors(prev => unlessSame(prev, r.data))).catch(() => {});
      api.get('/editors/workload').then(r => setEditorWorkload(prev => unlessSame(prev, r.data))).catch(() => {});
      api.get('/editors/recommend').then(r => setEditorRecs(prev => unlessSame(prev, r.data))).catch(() => {});
      api.get('/users').then(r => setPms(prev => unlessSame(prev, r.data.filter(u => u.role === 'staff')))).catch(() => {});
      api.get('/editor-payments/summary').then(r => setUnpaidProjectIds(prev => { const ids = [...(r.data.unpaid_project_ids || [])].sort(); return sameRow([...prev].sort(), ids) ? prev : new Set(ids); })).catch(() => {});
    }
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live workload counts: local ±1 on successful (re/un)assignment + server-truth
  // refetch. +1 is always correct (assign-editor sets editor_status_id to -1/0,
  // both counted); -1 only if the row was actually in the counted set [-1,0,1].
  const refreshWorkload = useCallback(() => {
    api.get('/editors/workload').then(r => setEditorWorkload(prev => unlessSame(prev, r.data))).catch(() => {});
  }, []);
  const bumpWorkload = useCallback((newEditorId, project) => {
    const prevId = project?.editor_id;
    const prevCounted = prevId && [-1, 0, 1].includes(project?.editor_status_id);
    setEditorWorkload(prev => {
      if (prevId === newEditorId) return prev;
      const next = { ...prev };
      if (prevCounted && next[prevId]) next[prevId] = { ...next[prevId], upcoming: Math.max(0, next[prevId].upcoming - 1) };
      if (newEditorId && next[newEditorId]) next[newEditorId] = { ...next[newEditorId], upcoming: next[newEditorId].upcoming + 1 };
      return next;
    });
    refreshWorkload();
  }, [refreshWorkload]);

  // Business health for clients
  useEffect(() => {
    if (user?.role === 'client') {
      api.get('/business-health').then(r => setBusinessHealth(r.data)).catch(() => {});
    }
  }, [user?.role]);
  useEffect(() => {
    if (user?.role !== 'client') return;
    const refetch = () => api.get('/style-profile').then(r => setStyleProfile(r.data)).catch(() => {});
    window.addEventListener('focus', refetch);
    const onVis = () => { if (!document.hidden) refetch(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('focus', refetch); document.removeEventListener('visibilitychange', onVis); };
  }, [user?.role]);

  // Morning Brief board filter: id set derived from the card's CURRENT items.
  // Recomputes on every actionItems poll → hard cut when a project stops qualifying.
  const briefFilterIds = useMemo(() => {
    if (!briefFilterKey || !actionItems) return null;
    return new Set((actionItems[briefFilterKey] || []).map(it => it.id));
  }, [briefFilterKey, actionItems]);

  // Waiting column (awaiting_client filter): id → days_silent from the SAME
  // action-items payload the filter already consumes (zero projection change).
  // Rebuilt only when the awaiting_client slice CHANGES IN CONTENT. The action-items
  // payload is refetched every 15 s and other slices of it are not stably ordered, so
  // keying on the whole payload handed every memoised row a fresh Map each poll.
  const awaitingRef = useRef({ key: '', map: new Map() });
  const awaitingDaysById = useMemo(() => {
    const list = actionItems?.awaiting_client || [];
    const key = canonical(list);
    if (awaitingRef.current.key !== key) awaitingRef.current = { key, map: new Map(list.map(it => [it.id, it.days_silent ?? null])) };
    return awaitingRef.current.map;
  }, [actionItems]);

  // No-Editor filter row-retention (assign→move two-step operator workflow).
  // Session-scoped ref accumulates every id that ever appeared in
  // briefFilterIds while briefFilterKey==='no_editor'. Reset on toggle off / switch
  // via toggleBriefFilter. A row that drops from briefFilterIds (editor now
  // assigned) but is still at admin_status_id===2 (Files Processed) gets pinned
  // into the filtered view until (a) status leaves Files Processed or (b) the
  // filter is cleared/re-engaged. Card tile count + email-alert predicate
  // untouched (backend truth unchanged; this is a view-retention overlay only).
  const noEditorSessionSeenIds = useRef(new Set());
  useEffect(() => {
    if (briefFilterKey === 'no_editor' && briefFilterIds) {
      briefFilterIds.forEach(id => noEditorSessionSeenIds.current.add(id));
    }
  }, [briefFilterKey, briefFilterIds]);

  // Pinned rows = seen-this-session + dropped-from-set + editor assigned +
  // still Files Processed. Derived off local projects[] so the pin survives
  // polls; naturally clears when admin_status_id !== 2.
  const noEditorPinnedIds = useMemo(() => {
    if (briefFilterKey !== 'no_editor' || !briefFilterIds) return new Set();
    const pinned = new Set();
    for (const p of projects) {
      if (briefFilterIds.has(p.id)) continue;
      if (!noEditorSessionSeenIds.current.has(p.id)) continue;
      if (!p.editor_id) continue;
      if (p.admin_status_id !== 2) continue;
      pinned.add(p.id);
    }
    return pinned;
  }, [projects, briefFilterKey, briefFilterIds]);

  /* Open a project from the list. Normally the drawer keeps whatever tab you were
     last on, which is right — except when you arrived from a brief card, because
     then the card already told us what you came to look at.
     `file_requests` lands on Files, where both the editor and admin panels live.
     Without it the escalation card drops you on Review, which says nothing about
     the escalation, and you have to know where to look — the same dead end the
     notification routing had. Defined once and used by BOTH the desktop row and
     the mobile card: they are separate render paths and this is exactly the kind
     of change that otherwise lands on only one of them. */
  const openProject = useCallback((project) => {
    if (briefFilterKey === 'file_requests') setDrawerTab('files');
    setSelectedProject(project);
  }, [briefFilterKey]);
  // Once a minute, so a memoised row's time-derived chips (hours left, days overdue,
  // late start) move even when nothing in its document changed.
  const [tick, setTick] = useState(() => Math.floor(Date.now() / 60000));
  useEffect(() => {
    // Aligned to the minute boundary so the once-a-minute re-render of every row lands at
    // a predictable moment (relative times such as "Nudged 3m ago" advance on it).
    let iv; const t = setTimeout(() => { setTick(Math.floor(Date.now() / 60000)); iv = setInterval(() => setTick(Math.floor(Date.now() / 60000)), 60000); }, 60000 - (Date.now() % 60000));
    return () => { clearTimeout(t); if (iv) clearInterval(iv); };
  }, []);

  const briefSortSnapshot = useRef(null);
  const toggleBriefFilter = (key) => {
    if (briefFilterKey === key) {
      setBriefFilterKey(null);
      if (briefCollapseSnapshot.current !== null) { setCollapsed(briefCollapseSnapshot.current); briefCollapseSnapshot.current = null; }
      if (key === 'awaiting_client' && briefSortSnapshot.current) { setSortCol(briefSortSnapshot.current.col); setSortDir(briefSortSnapshot.current.dir); briefSortSnapshot.current = null; }
      if (key === 'no_editor') noEditorSessionSeenIds.current = new Set();
    } else {
      if (briefFilterKey === null) { briefCollapseSnapshot.current = collapsed; setCollapsed({}); }
      if (key === 'awaiting_client') {
        // engage triage sort: longest-waiting first; restored on exit
        briefSortSnapshot.current = { col: sortCol, dir: sortDir };
        setSortCol('waiting'); setSortDir('desc');
      } else if (briefFilterKey === 'awaiting_client' && briefSortSnapshot.current) {
        setSortCol(briefSortSnapshot.current.col); setSortDir(briefSortSnapshot.current.dir); briefSortSnapshot.current = null;
      }
      // Switching AWAY from no_editor also resets its session-seen set so
      // re-engaging later gives an honest fresh view.
      if (briefFilterKey === 'no_editor' && key !== 'no_editor') noEditorSessionSeenIds.current = new Set();
      setBriefFilterKey(key);
    }
  };
  const clearBriefFilter = () => { if (briefFilterKey) toggleBriefFilter(briefFilterKey); };

  // Filtered projects
  const filtered = useMemo(() => {
    let result = projects;
    if (briefFilterIds) {
      // No-Editor filter widens to include pinned rows (assigned-but-still
      // Files-Processed, seen this session) — see noEditorPinnedIds above.
      if (briefFilterKey === 'no_editor' && noEditorPinnedIds.size) {
        result = result.filter(p => briefFilterIds.has(p.id) || noEditorPinnedIds.has(p.id));
      } else {
        result = result.filter(p => briefFilterIds.has(p.id));
      }
    }
    if (typeFilter !== 'all') result = result.filter(p => p.project_type === typeFilter);
    if (publicityFilter !== 'all') result = result.filter(p => p.publicity_preference === publicityFilter);
    if (!search) return result;
    return result.filter(p => projectMatchesSearch(p, search));
  }, [projects, search, typeFilter, publicityFilter, briefFilterIds, briefFilterKey, noEditorPinnedIds]);

  // Lazily learn whether any ARCHIVED project matches what you just typed.
  // Fires only on the main board, only while the toggle is off, and only once per
  // visit — a single extra request the first time you search, never on load.
  useEffect(() => {
    if (archived || trashed || includeArchived) return;
    if (!search.trim() || archivedPeek !== null) return;
    let cancelled = false;
    api.get('/projects', { params: { archived: true } })
      .then(r => { if (!cancelled) setArchivedPeek(r.data || []); })
      .catch(() => { if (!cancelled) setArchivedPeek([]); });
    return () => { cancelled = true; };
  }, [search, archived, trashed, includeArchived, archivedPeek]);

  const archivedMatchCount = useMemo(() => {
    if (!search.trim() || includeArchived || !archivedPeek) return 0;
    return archivedPeek.filter(p => projectMatchesSearch(p, search)).length;
  }, [archivedPeek, search, includeArchived]);

  // Group projects by status, sort within each group
  const groups = useMemo(() => {
    const statusEntries = Object.entries(cfg.statuses);
    const editorOrder = [-1, 0, 1, 2, 4, 3, 5];
    if (cfg.track === 'editor') {
      statusEntries.sort((a, b) => editorOrder.indexOf(parseInt(a[0])) - editorOrder.indexOf(parseInt(b[0])));
    } else {
      statusEntries.sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
    }

    // Sort helper for within-group sorting
    const getSortValue = (p, col) => {
      switch (col) {
        case 'order_date': return p.created_at || '';
        case 'project': return (p.title || '').toLowerCase();
        case 'product': return (p.product_name || (p.products || [])[0] || '').toLowerCase();
        case 'client': return (p.client_name || '').toLowerCase();
        case 'event': return p.event_date || '';
        case 'eta': return p.eta || p.editor_due_date || '';
        case 'editor': return (p.editor_name || '').toLowerCase();
        case 'pm': return (p.pm_name || '').toLowerCase();
        case 'program': return getEditingProgram(p).toLowerCase();
        case 'status': return p[cfg.key] || 0;
        case 'waiting': { const d = awaitingDaysById.get(p.id); return d == null ? -1 : d; }
        default: return '';
      }
    };

    return statusEntries.map(([sid, name]) => {
      const id = parseInt(sid);
      const items = filtered.filter(p => (p[cfg.key] ?? 1) === id);
      // Apply within-group sort
      items.sort((a, b) => {
        const va = getSortValue(a, sortCol);
        const vb = getSortValue(b, sortCol);
        let cmp = 0;
        if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
        else cmp = String(va).localeCompare(String(vb));
        return sortDir === 'asc' ? cmp : -cmp;
      });
      return { id, name, items, color: cfg.colors[id] || '#94A3B8' };
    });
  }, [filtered, cfg, sortCol, sortDir, awaitingDaysById]);

  /* Row-checkbox toggle. `shift` selects/deselects the whole VISIBLE range between the last
     clicked row and this one (collapsed groups are skipped: what you cannot see you did not
     select). Runs inside a transition from RowCheckbox, so the 500-row re-render never
     blocks the click. */
  const boardShapeRef = useRef({ groups: [], collapsed: {} });
  boardShapeRef.current = { groups, collapsed };
  const toggleSelection = useCallback((id, next, shift) => {
    // Capture the anchor NOW: the updater below runs later, inside the transition, after
    // the ref has already moved on to this row (first version selected only one row).
    const anchor = selectionAnchorRef.current;
    selectionAnchorRef.current = id;
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (shift && anchor && anchor !== id) {
        // read the CURRENT groups/collapsed through refs: this callback is a prop of every
        // memoised row, so it must not change identity when the board re-groups
        const { groups, collapsed } = boardShapeRef.current;
        const order = groups.flatMap(g => (collapsed[g.id] ? [] : g.items.map(p => p.id)));
        const a = order.indexOf(anchor), b = order.indexOf(id);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let x = lo; x <= hi; x++) { if (next) n.add(order[x]); else n.delete(order[x]); }
          return n;
        }
      }
      if (next) n.add(id); else n.delete(id);
      return n;
    });
  }, []);

  const toggleGroup = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  // ── Optimistic status change ──
  const handleStatusChange = useCallback(async (projectId, newStatusId, force = false) => {
    if (!cfg.track) return; // clients can't change
    const oldProjects = [...projectsRef.current];
    // Optimistic: move row immediately
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, [cfg.key]: newStatusId } : p));
    try {
      const res = await api.patch(`/projects/${projectId}/status`, force ? { track: cfg.track, status_id: newStatusId, force: true } : { track: cfg.track, status_id: newStatusId });
      setProjects(prev => prev.map(p => p.id === projectId ? res.data : p));
      if (selectedProjectRef.current?.id === projectId) setSelectedProject(res.data);
      toast.success((() => {
        if (user.role !== 'editor') return 'Status updated';
        switch (newStatusId) {
          case 1: return 'Marked as Editing In Progress. Let\'s go!';
          case 2: return 'First draft submitted. Now we wait for client feedback.';
          case 3: return 'Working on revisions. You got this!';
          case 4: return 'New draft submitted. Fingers crossed for approval!';
          default: return 'Status updated';
        }
      })());
    } catch (e) {
      setProjects(oldProjects); // rollback
      // A move off Project Requested with no footage the portal can pull is refused in words (lib/handMove.js).
      // Admin is asked, and may make it anyway; anyone else is told what to do instead.
      const stop = stopOf(e);
      if (!stop) { toast.error('Status update failed'); return; }
      if (!force && stop.canOverride && await confirm({ title: ASK_TITLE, message: askWords(stop), confirmLabel: ASK_YES, cancelLabel: ASK_NO })) {
        await handleStatusChange(projectId, newStatusId, true);
        return;
      }
      if (!stop.canOverride) toast.error(stop.message, { duration: 12000 });
    }
  }, [cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  // The bulk bar's status change. Each project is asked on its own, so one that cannot move (nothing to pull:
  // lib/handMove.js) does not stop the rest, and its reason is said. No "move it anyway" from here: a project that
  // needs one is opened and moved on its own.
  const bulkStatus = async (v) => {
    const ids = [...selectedIds];
    const results = await Promise.allSettled(ids.map(id => api.put(`/projects/${id}/admin-status`, { status_id: parseInt(v) })));
    const failed = results.filter(r => r.status === 'rejected');
    const stops = failed.map(r => stopOf(r.reason)).filter(Boolean);
    const moved = ids.length - failed.length;
    if (moved) toast.success(`${moved} project${moved === 1 ? '' : 's'} updated`);
    if (stops.length) toast.error(bulkWords(stops.length, stops[0]), { duration: 12000 });
    if (failed.length > stops.length) toast.error('Failed');
    if (moved || stops.length) { setSelectedIds(new Set()); fetchProjects(); }
  };

  // ── DnD handlers ──
  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const overId = String(over.id);
    if (!overId.startsWith('group-')) return;
    const newStatusId = parseInt(over.data?.current?.statusId);
    const project = active.data?.current?.project;
    if (!project || !newStatusId || project[cfg.key] === newStatusId) return;
    handleStatusChange(project.id, newStatusId);
  };

  const handleCreateProject = async () => {
    if (!newTitle.trim()) return;
    try {
      const payload = { title: newTitle, event_date: newEventDate, brand_name: newBrand };
      // Admin: include client and product
      if ((user.role === 'admin' || user.role === 'staff') && newClientId) {
        payload.client_id = newClientId;
        const client = allClients.find(c => c.id === newClientId);
        if (client) {
          payload.client_name = client.name;
          payload.studio_name = client.company_name || '';
        }
      }
      // Admin: product comes from the real catalog, or free text via Custom.
      //
      // Catalog path writes the SAME structured line_items shape a client order
      // writes (see lib/adminProductCatalog.js). That is what connects the
      // project to editor pay rates, SLA/due-date auto-calc, and must-haves
      // category resolution — all of which read line_items, not product_name.
      // The Custom path keeps the legacy product_name-only behaviour, which is
      // why custom products can't resolve an editor pay rate.
      const amountNum = parseFloat(newAmount);
      const hasAmount = !isNaN(amountNum) && amountNum >= 0;
      const catalogProduct = newProductKey && newProductKey !== CUSTOM_PRODUCT_KEY
        ? findCatalogProduct(newProductKey) : null;

      if (catalogProduct) {
        const item = catalogProduct.buildLineItem({ nle: newCullingNle });
        // Operator-entered amount is authoritative for revenue; fall back to
        // catalog price if the field was cleared.
        if (hasAmount) item.amount = amountNum;
        payload.line_items = [item];
        payload.products = [item.label];
        payload.product_name = item.label;
        payload.project_type = 'video';       // catalog is video-only by design
        payload.is_doc_edit = !!item.is_doc_edit;
        payload.subtotal = item.amount;
        payload.total = item.amount;
      } else if (newProductId.trim()) {
        payload.products = [newProductId.trim()];
        payload.product_name = newProductId.trim();
        const pLower = newProductId.trim().toLowerCase();
        payload.project_type = (pLower.includes('photo') || pLower.includes('cull') || pLower.includes('retouch')) ? 'photo' : 'video';
        // No line_items — server falls back to subtotal for pricing.
        if (hasAmount) { payload.subtotal = amountNum; payload.total = amountNum; }
      }
      // Payments are handled outside the portal; the operator states the status.
      payload.payment_status = newIsPaid ? 'paid' : 'pending';
      await api.post('/projects', payload);
      toast.success('Project created');
      setNewTitle(''); setNewEventDate(''); setNewBrand(''); setNewClientId(''); setNewProductId('');
      setNewProductKey(''); setNewCullingNle(''); setNewAmount(''); setNewIsPaid(true);
      setClientBrands([]); setShowNewProject(false);
      fetchProjects();
    } catch { toast.error('Failed'); }
  };

  // ── Set editor due date (auto-computes ETA) ──
  // Three-path flow when an admin/staff edits a project that has an editor:
  //   1. Fetch dry-run preview of cascade impact.
  //   2. If preview has zero affected + zero in-between → silent PUT, no modal.
  //   3. Else open CascadePreviewDialog. Admin picks:
  //        - Shift all later     → PUT with default cascade
  //        - Only shift this     → PUT with skip_cascade=true
  //        - Cancel              → revert optimistic state, no PUT
  // Cancel-means-nothing invariant: PUT only fires after one of the two
  // confirm paths. On cancel we restore the snapshot taken before the
  // optimistic UI write.
  const performDueDatePut = useCallback(async (projectId, dueDate, skipCascade, oldProjectsSnapshot, laterMode) => {
    try {
      const res = await api.put(`/projects/${projectId}`, {
        editor_due_date: dueDate,
        ...(skipCascade ? { skip_cascade: true } : {}),
        // a later move past other jobs: push them back too ("push", the default) or leave them (operator, 2026-10-05)
        ...(!skipCascade && laterMode ? { later_mode: laterMode } : {}),
      });
      if (res.data) {
        setProjects(prev => prev.map(p => p.id === projectId ? { ...p, editor_due_date: res.data.editor_due_date, eta: res.data.eta } : p));
      }
      // Always refetch — cascade may have shifted other rows
      if (!skipCascade) fetchProjects();
      toast.success(!dueDate ? 'Due date cleared' : skipCascade ? 'Due date updated (cascade skipped)' : 'Due date updated');
    } catch {
      setProjects(oldProjectsSnapshot);
      toast.error('Failed to set due date');
    }
  }, [fetchProjects]);

  const handleSetDueDate = useCallback(async (projectId, dueDate) => {
    const oldProjects = [...projectsRef.current];
    const proj = projectsRef.current.find(p => p.id === projectId);
    const eta = dueDate ? (() => { const d = new Date(dueDate + 'T00:00:00'); d.setDate(d.getDate() + 3); return d.toISOString().split('T')[0]; })() : '';
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, editor_due_date: dueDate, eta } : p));

    // Skip the preview path entirely for: clearing a date, no editor assigned,
    // non-admin caller, or same-value edits (delta=0).
    const isAdminLike = user?.role === 'admin' || user?.role === 'staff';
    if (!dueDate || !isAdminLike || !proj?.editor_id || proj?.editor_due_date === dueDate) {
      return performDueDatePut(projectId, dueDate, false, oldProjects);
    }

    try {
      const r = await api.get(`/projects/${projectId}/cascade-preview`, { params: { new_date: dueDate, later_mode: 'push' } });
      const preview = r.data || {};
      // A later move that jumps other jobs always asks, even if nothing else would move (operator, 2026-10-05):
      // before, a jumped job never showed in the preview, so the board saved silently and the job fell behind.
      const hasImpact = (preview.moves?.length || 0) > 0 || (preview.jumped?.length || 0) > 0;
      if (!hasImpact) {
        // No queue impact — same outcome as the old path: write the single project, no modal.
        return performDueDatePut(projectId, dueDate, false, oldProjects);
      }
      // Open the modal. Decision handlers (below) consume pendingDateEdit + cascadePreview.
      setCascadePreview(preview);
      setPendingDateEdit({ projectId, projectTitle: proj?.title || '', newDate: dueDate, oldProjectsSnapshot: oldProjects,
                           laterMode: preview.later_mode || 'push' });
    } catch (err) {
      // Preview fetch failed — DO NOT silently fall through to cascade. The
      // entire point of the modal is to surface impact before committing.
      // When the preview is unavailable, the safest outcome is to surface the
      // failure and let admin choose: write single project (skip cascade),
      // retry the preview, or cancel. NEVER cascade silently here.
      // Carry the server's reason through. A 400 from cascade-preview names the
      // exact records with unreadable due dates; without this the dialog could
      // only say "preview unavailable", which is why a data bug needed
      // investigation instead of reading the message. Unstructured/500 failures
      // fall back to the generic copy in CascadePreviewDialog.
      const detail = err?.response?.data?.detail;
      setCascadePreview({
        error: true,
        error_detail: typeof detail === 'string' ? detail : '',
        old_date: proj?.editor_due_date || '', new_date: dueDate,
      });
      setPendingDateEdit({ projectId, projectTitle: proj?.title || '', newDate: dueDate, oldProjectsSnapshot: oldProjects });
    }
  }, [user?.role, performDueDatePut]);

  const handleCascadeConfirm = useCallback(async () => {
    if (!pendingDateEdit) return;
    const { projectId, newDate, oldProjectsSnapshot, laterMode } = pendingDateEdit;
    setCascadeSubmitting(true);
    await performDueDatePut(projectId, newDate, false, oldProjectsSnapshot, laterMode);
    setCascadeSubmitting(false);
    setCascadePreview(null);
    setPendingDateEdit(null);
  }, [pendingDateEdit, performDueDatePut]);

  const handleCascadeSkip = useCallback(async () => {
    if (!pendingDateEdit) return;
    const { projectId, newDate, oldProjectsSnapshot } = pendingDateEdit;
    setCascadeSubmitting(true);
    await performDueDatePut(projectId, newDate, true, oldProjectsSnapshot);
    setCascadeSubmitting(false);
    setCascadePreview(null);
    setPendingDateEdit(null);
  }, [pendingDateEdit, performDueDatePut]);

  // Push the jumped jobs back, or leave them: re-fetch the preview for that choice so the dialog shows exactly what
  // Apply will write (the server computes both from the same router).
  const handleLaterModeChange = useCallback(async (mode) => {
    if (!pendingDateEdit || pendingDateEdit.laterMode === mode) return;
    const { projectId, newDate } = pendingDateEdit;
    setCascadeSubmitting(true);
    try {
      const r = await api.get(`/projects/${projectId}/cascade-preview`, { params: { new_date: newDate, later_mode: mode } });
      setCascadePreview(r.data || {});
      setPendingDateEdit(prev => (prev ? { ...prev, laterMode: mode } : prev));
    } catch {
      toast.error('Could not update the preview');
    }
    setCascadeSubmitting(false);
  }, [pendingDateEdit]);

  // Retry path from the preview-error state. Re-fetches /cascade-preview;
  // on success replaces the error state with the real preview, on failure
  // re-stamps the error so the admin can choose again.
  const handleCascadeRetry = useCallback(async () => {
    if (!pendingDateEdit) return;
    const { projectId, newDate } = pendingDateEdit;
    setCascadeSubmitting(true);
    try {
      const r = await api.get(`/projects/${projectId}/cascade-preview`, { params: { new_date: newDate, later_mode: pendingDateEdit.laterMode || 'push' } });
      const preview = r.data || {};
      const hasImpact = (preview.moves?.length || 0) > 0 || (preview.jumped?.length || 0) > 0;
      if (!hasImpact) {
        // Retry surfaced that there's no actual cascade impact — write the
        // single project and close the modal (matches the no-impact path).
        await performDueDatePut(projectId, newDate, false, pendingDateEdit.oldProjectsSnapshot, pendingDateEdit.laterMode);
        setCascadePreview(null);
        setPendingDateEdit(null);
      } else {
        setCascadePreview(preview);
      }
    } catch {
      // Re-stamp the error state so the admin can choose Skip or Cancel.
      setCascadePreview(prev => ({ error: true, old_date: prev?.old_date, new_date: prev?.new_date }));
    }
    setCascadeSubmitting(false);
  }, [pendingDateEdit, performDueDatePut]);

  const handleCascadeCancel = useCallback(() => {
    if (!pendingDateEdit) return;
    // Revert optimistic UI write — no PUT fired, no DB mutation.
    setProjects(pendingDateEdit.oldProjectsSnapshot);
    setCascadePreview(null);
    setPendingDateEdit(null);
  }, [pendingDateEdit]);

  const refreshSelected = () => {
    fetchProjects();
    if (selectedProject) api.get(`/projects/${selectedProject.id}`).then(r => setSelectedProject(r.data)).catch(() => {});
  };

  const activeProject = activeId ? projects.find(p => p.id === activeId) : null;

  return (
    <div className="flex h-full overflow-hidden" data-testid="dashboard-page">
      {/* Left: Grouped Board — scrolls vertically. Its testid is the client site look's scope (site-look.css), which
          keeps the drawer, a sibling, out of the board's restyle. */}
      <div className="flex-1 min-w-0 overflow-auto p-4 sm:p-6 lg:p-8" data-testid="board-column">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 animate-stagger-1">
          <div>
            <h1 className="font-sans text-lg font-semibold text-zinc-100 flex items-center gap-2.5">
              {trashed ? 'Trash' : archived ? 'Archived' : user.role === 'editor' ? 'Projects' : user.role === 'client' ? 'Projects' : 'Projects'}
              <span data-testid="board-count-badge" className="tap-auto inline-flex items-center leading-none text-[11px] font-sans font-medium text-zinc-500 bg-ug-surface px-2 py-1 rounded-full">{briefFilterKey && !archived && !trashed ? `${filtered.length} of ${projects.length}` : projects.length}{trashed ? ' · auto-deleted after 30 days' : !archived ? ' · grouped by status' : ''}</span>
            </h1>
          </div>
          {(user.role === 'admin' || user.role === 'client') && !archived && !trashed && (
            <Button data-testid="new-project-btn" onClick={() => {
              if (user.role === 'client') {
                const { pct } = calcCompleteness(styleProfile);
                if (pct < 100) {
                  toast.error('Please complete your Style Profile (100%) before placing an order.');
                  setTimeout(() => navigate('/style-profile'), 1500);
                  return;
                }
                navigate('/new-order');
              } else {
                if (!showNewProject) {
                  // Fetch clients for the form
                  api.get('/users').then(r => setAllClients(r.data.filter(u => u.role === 'client'))).catch(() => {});
                }
                setShowNewProject(!showNewProject);
              }
            }}
              className="ug-cta tap-auto font-sans text-sm shadow-none">
              <span className="ug-cta-inner"><Plus className="w-4 h-4" /> New Project</span>
            </Button>
          )}
        </div>

        {/* Must-Haves intro modal (client only) — self-guarded: server-computed
            eligibility, once per session, never stacks with anything (ProductTour
            is manual-only; no other login-time modal exists for clients). */}
        {user.role === 'client' && !archived && !trashed && <MustHavesIntro />}
        {/* The one-time time survey (editors only): a PROMPT, required before the board,
            on every view of it. Song Approval and the announcement queue wait behind it. */}
        {user.role === 'editor' && <EditorTimeSurvey />}
        {/* One-time Song Approval announcement — the component gates on role and
            on whether this editor has already acknowledged it. */}
        {user.role === 'editor' && !archived && !trashed && <SongApprovalIntro />}
        {/* "What's new" announcements, every role (clients since the song-choices
            notice). Self-gating: asks the server what this user is owed, waits behind
            any PROMPT, and stands down for the login if another
            ANNOUNCEMENT is already on screen. At most one shows. Queue order and the
            prompt/announcement distinction live in AnnouncementQueue.js. */}
        {!archived && !trashed && <AnnouncementQueue />}

        {/* Trustpilot review-ask modal — Layer 1 in-portal peak-emotion prompt.
            Server-gated (cohort filter + cooldown + permanent-skip); no cross-role
            leakage — client-only fetch above sets reviewAsk. Waits for Must-Haves
            intro to dismiss so the two prompts never stack. */}
        {reviewAsk?.eligible && mustHavesDismissed && user.role === 'client' && !archived && !trashed && (
          <ReviewAskModal
            project={reviewAsk.project}
            approvedCount={reviewAsk.approved_count}
            onClose={() => setReviewAsk(null)} />
        )}

        {/* "PM on duty" banner — display-only v1. Shown for editors (primary),
            admin/staff/billing (harmless), NOT client (out of scope). Server value
            {user_id, name}; empty state renders nothing (no placeholder). */}
        {pmOnDuty?.name && user.role !== 'client' && !archived && !trashed && (
          <div
            data-testid="pm-on-duty-banner"
            className="tap-auto mb-4 rounded-lg border border-oxidized-teal/30 bg-oxidized-teal/[0.08] px-4 py-2.5 flex items-center gap-3 animate-stagger-1"
            role="status"
            aria-live="polite">
            <span className="text-oxidized-teal text-base leading-none" aria-hidden="true">●</span>
            <div className="flex flex-col leading-tight">
              <span className="font-sans text-[11px] font-bold uppercase tracking-wider text-oxidized-teal">
                PM on duty:{' '}
                <span data-testid="pm-on-duty-name" className="text-zinc-100 tracking-wide">
                  {pmOnDuty.name}
                </span>
              </span>
              <span className="font-sans text-[10px] text-slate-500 mt-0.5">
                tag <span className="text-ug-gold">@{pmOnDuty.name}</span> with any questions
              </span>
            </div>
          </div>
        )}

        {/* Incomplete-multipart gauge — HIDDEN 2026-07-15 per operator ("not useful"). Component file, backend
             endpoints (/api/admin/storage/incomplete-multipart, /multipart-abort), audit collection, and daily
             0-part >7d auto-prune all preserved. Restore = uncomment the import at the top of this file and the
             render line below. */}
        {/* {(user.role === 'admin' || user.role === 'staff') && !archived && !trashed && <MultipartGauge />} */}

        {/* Style Profile Completeness Bar (client only) */}
        {user.role === 'client' && !archived && styleProfile && (() => {
          const { pct, filled, total } = calcCompleteness(styleProfile);
          if (pct >= 100) return null;
          const hasProfile = styleProfile.profile_type;
          return (
            <div className="mb-4 animate-stagger-2" data-testid="profile-completeness-bar">
              <div className="p-5 rounded-xl border bg-ug-gold/[0.06] border-ug-gold/20">
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-2">
                    <GemProgress pct={pct} />
                    <span className="font-sans text-[11px] font-medium uppercase tracking-wider text-ug-gold">
                      {hasProfile ? `Style Profile — ${pct}% Complete` : 'Action Required: Style Profile Not Set Up'}
                    </span>
                  </div>
                  <button onClick={() => {
                    const v = styleProfile.video || {};
                    const p = styleProfile.photo || {};
                    const has = x => x && x.length > 0 && x !== '';
                    const pt = styleProfile.profile_type;
                    let missing = [];
                    let section = '';
                    if (pt === 'video' || pt === 'both') {
                      if (!has(v.story_telling)) { missing.push('Story Telling'); if (!section) section = 'story'; }
                      if (!has(v.client_language)) { missing.push('Client Language'); if (!section) section = 'story'; }
                      if (!has(v.nle_software)) { missing.push('Editing Software'); if (!section) section = 'nle'; }
                      if (!(v.reference_videos || []).some(u => has(u))) { missing.push('Highlight film sample'); if (!section) section = 'reference'; }
                    }
                    if (pt === 'photo' || pt === 'both') {
                      if (!has(p.white_balance)) { missing.push('White Balance'); if (!section) section = 'photo-look'; }
                      if (!has(p.exposure_contrast)) { missing.push('Exposure & Contrast'); if (!section) section = 'photo-look'; }
                      if (!has(p.skin_tone_bias)) { missing.push('Skin Tone'); if (!section) section = 'photo-look'; }
                      if (!has(p.bw_usage)) { missing.push('B&W Usage'); if (!section) section = 'photo-look'; }
                      if (!has(p.crop_straighten)) { missing.push('Crop & Straighten'); if (!section) section = 'photo-look'; }
                      if (!has(p.burst_duplicates)) { missing.push('Burst/Duplicates'); if (!section) section = 'photo-culling'; }
                      if (!has(p.editor_notes)) { missing.push('Editor Notes'); if (!section) section = 'photo-notes'; }
                      if (!has(p.culling_notes)) { missing.push('Culling Notes'); if (!section) section = 'photo-culling'; }
                      if (!(has(p.preset_filename) || (p.reference_galleries || []).some(u => has(u)))) { missing.push('Preset or Reference Gallery'); if (!section) section = 'photo-look'; }
                    }
                    if (missing.length > 0) toast.error(`Missing: ${missing.join(', ')}`);
                    navigate(`/style-profile${section ? `#${section}` : ''}`);
                  }} data-testid="complete-profile-link"
                    className="flex items-center gap-1.5 font-sans text-[11px] font-medium uppercase tracking-wider px-3 py-1.5 rounded-lg bg-ug-gold/15 text-ug-gold hover:bg-ug-gold/25 transition-colors">
                    {hasProfile ? 'Complete Profile' : 'Set Up Now'} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                {hasProfile && (
                  <>
                    <Progress value={pct} className="h-1.5 bg-ug-surface" data-testid="profile-progress" />
                    <p className="text-[11px] text-zinc-500 mt-2">You must complete all fields before placing an order. {filled} of {total} fields done.</p>
                  </>
                )}
                {!hasProfile && (
                  <p className="text-[12px] text-zinc-400 mt-1">You must complete your style profile before placing any orders. We need to know your editing preferences so we can match your unique look from the first cut.</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Business Health — compact one-liner, only shows after 3+ completed */}
        {user.role === 'client' && businessHealth && <BusinessHealthBanner stats={businessHealth} />}
        {user.role === 'client' && !businessHealth && (
          <div className="mb-4 px-4 py-2.5 rounded-xl border border-ug-border bg-ug-raised flex items-center gap-3" data-testid="business-health-skeleton">
            <Skeleton className="w-4 h-4 rounded-full bg-white/[0.06]" />
            <Skeleton className="h-3 flex-1 max-w-md bg-white/[0.04]" />
            <Skeleton className="h-3 w-16 bg-white/[0.04]" />
          </div>
        )}

        {/* The standing line (lib/keepLink.js, Ryan 2026-10-26), once above the list while any of the client's open
            orders is footage they sent as a link, before its first draft: the board is where "Editing In Progress" reads
            as "they have my footage". The server decides per order (keep_footage_link). */}
        {user.role === 'client' && !archived && !trashed && boardNeedsLine(projects) && (
          <KeepFootageLink testId="keep-footage-link-board" className="mb-4" />
        )}

        {showNewProject && (
          <div className="mb-5 p-4 bg-ug-raised/60 rounded-md border border-ug-gold/15 space-y-3 animate-stagger-1 backdrop-blur-sm" data-testid="new-project-form">
            <div className="flex gap-3 items-end flex-wrap">
              <div className="flex-1 min-w-[180px] space-y-1">
                <label className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Project Title</label>
                <Input data-testid="new-project-title" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. Smith-Jones Wedding" className="bg-ug-surface border-ug-border-strong text-zinc-100 h-9" />
              </div>
              <div className="w-40 space-y-1">
                <label className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Event Date</label>
                <Input data-testid="new-project-date" type="date" value={newEventDate} onChange={e => setNewEventDate(e.target.value)} className="bg-ug-surface border-ug-border-strong text-zinc-100 h-9" />
              </div>
            </div>
            {(user.role === 'admin' || user.role === 'staff') && (
              <div className="flex gap-3 items-end flex-wrap">
                <div className="w-52 space-y-1">
                  <label className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Client *</label>
                  <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" aria-expanded={clientSearchOpen} data-testid="new-project-client"
                        className="w-full justify-between bg-ug-surface border-ug-border-strong text-zinc-100 h-9 text-sm font-normal">
                        {newClientId ? (allClients.find(c => c.id === newClientId)?.name || 'Select client') : 'Select client'}
                        <ChevronDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-0 bg-ug-raised border-ug-border-strong" align="start">
                      <Command className="bg-transparent">
                        <CommandInput placeholder="Search clients..." className="h-9 text-sm" />
                        <CommandList>
                          <CommandEmpty className="py-3 text-center text-xs text-zinc-500">No clients found</CommandEmpty>
                          <CommandGroup className="max-h-60 overflow-y-auto">
                            {[...allClients].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => (
                              <CommandItem key={c.id} value={`${c.name} ${c.company_name || ''} ${c.email}`} onSelect={() => {
                                setNewClientId(c.id);
                                setNewBrand('');
                                setClientSearchOpen(false);
                                api.get(`/style-profile/${c.id}`).then(r => {
                                  const brands = r.data?.brands || [];
                                  setClientBrands(brands);
                                  if (brands.length === 1) setNewBrand(brands[0]);
                                }).catch(() => setClientBrands([]));
                              }} className="text-zinc-100 text-xs cursor-pointer">
                                <Check className={`mr-2 h-3 w-3 ${newClientId === c.id ? 'opacity-100' : 'opacity-0'}`} />
                                {c.name} — {c.company_name || c.email}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="w-52 space-y-1">
                  <label className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Product *</label>
                  <Popover open={productSearchOpen} onOpenChange={setProductSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" aria-expanded={productSearchOpen} data-testid="new-project-product"
                        className="w-full justify-between bg-ug-surface border-ug-border-strong text-zinc-100 h-9 text-sm font-normal">
                        <span className="truncate">
                          {newProductKey === CUSTOM_PRODUCT_KEY
                            ? 'Custom / other...'
                            : (findCatalogProduct(newProductKey)?.name || 'Select product')}
                        </span>
                        <ChevronDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0 bg-ug-raised border-ug-border-strong" align="start">
                      <Command className="bg-transparent">
                        <CommandInput placeholder="Search products..." className="h-9 text-sm" />
                        <CommandList>
                          <CommandEmpty className="py-3 text-center text-xs text-zinc-500">No products found</CommandEmpty>
                          <CommandGroup className="max-h-60 overflow-y-auto">
                            {ADMIN_VIDEO_CATALOG.map(p => (
                              <CommandItem key={p.key} value={`${p.group} ${p.name}`} onSelect={() => {
                                setNewProductKey(p.key);
                                setNewProductId('');
                                if (p.key !== CULLING_KEY) setNewCullingNle('');
                                setNewAmount(String(p.price));   // prefill, operator can override
                                setProductSearchOpen(false);
                              }} className="text-zinc-100 text-xs cursor-pointer">
                                <Check className={`mr-2 h-3 w-3 shrink-0 ${newProductKey === p.key ? 'opacity-100' : 'opacity-0'}`} />
                                <span className="truncate">{p.name}</span>
                                <span className="ml-auto pl-2 text-[10px] text-slate-500 shrink-0">${p.price}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          <CommandGroup className="border-t border-ug-border">
                            <CommandItem key={CUSTOM_PRODUCT_KEY} value="custom other one-off" onSelect={() => {
                              setNewProductKey(CUSTOM_PRODUCT_KEY);
                              setNewCullingNle('');
                              setProductSearchOpen(false);
                            }} className="text-zinc-100 text-xs cursor-pointer" data-testid="new-project-product-custom">
                              <Check className={`mr-2 h-3 w-3 shrink-0 ${newProductKey === CUSTOM_PRODUCT_KEY ? 'opacity-100' : 'opacity-0'}`} />
                              Custom / other...
                            </CommandItem>
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                {newProductKey === CULLING_KEY && (
                  <div className="w-44 space-y-1">
                    <label className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Editing Program *</label>
                    <Select value={newCullingNle} onValueChange={setNewCullingNle}>
                      <SelectTrigger data-testid="new-project-nle" className="bg-ug-surface border-ug-border-strong text-zinc-100 h-9 text-sm"><SelectValue placeholder="Select NLE" /></SelectTrigger>
                      <SelectContent className="bg-ug-raised border-ug-border-strong">
                        {NLE_OPTIONS.map(n => <SelectItem key={n} value={n} className="text-zinc-100 text-xs">{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {newProductKey === CUSTOM_PRODUCT_KEY && (
                  <div className="w-60 space-y-1">
                    <label className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Custom Product *</label>
                    <Input data-testid="new-project-product-custom-name" value={newProductId} onChange={e => setNewProductId(e.target.value)} placeholder="e.g. Highlight 6 min" className="bg-ug-surface border-ug-border-strong text-zinc-100 h-9 text-sm" />
                    <p className="text-[10px] text-ug-status-red/80 leading-snug">Custom products don't match an editor pay rate, so editor pay computes as $0 and the due date falls back to 7 days.</p>
                  </div>
                )}
                <div className="w-28 space-y-1">
                  <label className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Amount ($)</label>
                  <Input data-testid="new-project-amount" type="number" min="0" step="0.01" value={newAmount} onChange={e => setNewAmount(e.target.value)} placeholder="0.00" className="bg-ug-surface border-ug-border-strong text-zinc-100 h-9 text-sm" />
                </div>
                <div className="flex items-center gap-2 h-9">
                  <Checkbox id="new-project-paid" data-testid="new-project-paid" checked={newIsPaid} onCheckedChange={v => setNewIsPaid(!!v)} />
                  <label htmlFor="new-project-paid" className="font-sans text-[11px] text-zinc-300 cursor-pointer tap-auto">Paid</label>
                </div>
                {clientBrands.length > 1 && (
                  <div className="w-44 space-y-1">
                    <label className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Brand</label>
                    <Select value={newBrand} onValueChange={setNewBrand}>
                      <SelectTrigger data-testid="new-project-brand" className="bg-ug-surface border-ug-border-strong text-zinc-100 h-9 text-sm"><SelectValue placeholder="Select brand" /></SelectTrigger>
                      <SelectContent className="bg-ug-raised border-ug-border-strong">
                        {clientBrands.map(b => <SelectItem key={b} value={b} className="text-zinc-100 text-xs">{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
            {user.role === 'client' && myBrands.length > 0 && (
              <div className="flex gap-3 items-end flex-wrap">
                <div className="w-44 space-y-1">
                  <label className="font-sans text-[10px] font-semibold uppercase tracking-wider text-ug-gold/70">Brand</label>
                  <Select value={newBrand} onValueChange={setNewBrand}>
                    <SelectTrigger data-testid="new-project-brand" className="bg-ug-surface border-ug-border-strong text-zinc-100 h-9 text-sm"><SelectValue placeholder="Select brand" /></SelectTrigger>
                    <SelectContent className="bg-ug-raised border-ug-border-strong">
                      {myBrands.map(b => <SelectItem key={b} value={b} className="text-zinc-100 text-xs">{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button data-testid="submit-new-project" onClick={handleCreateProject} className="bg-ug-gold text-obsidian hover:bg-ug-gold/90 font-sans text-xs font-medium h-9">Create</Button>
              <Button data-testid="cancel-new-project" variant="outline" onClick={() => setShowNewProject(false)} className="border-ug-border-strong text-slate-400 font-sans text-xs font-medium h-9">Cancel</Button>
            </div>
          </div>
        )}

        {/* Morning Brief — All roles */}
        {!actionItems && !archived && !trashed && (
          <div className="mb-4 rounded-xl border border-ug-border bg-ug-raised overflow-hidden shadow-ug-sm" data-testid="morning-brief-skeleton">
            <div className="px-5 py-3 border-b border-ug-border-subtle flex items-center justify-between">
              <Skeleton className="h-3 w-32 bg-white/[0.06]" />
              <Skeleton className="h-3 w-8 bg-white/[0.04]" />
            </div>
            <div className="p-4 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-3 w-1/4 bg-white/[0.06]" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-3 w-4/5 bg-white/[0.04]" />
                    <Skeleton className="h-3 w-3/5 bg-white/[0.04]" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {actionItems && !archived && !trashed && (
          briefCollapsed ? (
            <button onClick={() => setBriefCollapsed(false)} data-testid="expand-brief-btn"
              className="mb-5 w-full flex items-center justify-between px-4 py-2.5 rounded-xl border border-ug-border bg-ug-raised hover:border-ug-gold/25 transition-colors">
              <span className="text-xs text-zinc-400 font-sans">Morning Brief</span>
              <div className="flex items-center gap-2">
                {actionItems.total_items > 0 && <span className="bg-ug-status-red text-white text-[9px] font-mono font-bold min-w-[20px] h-5 px-1.5 rounded flex items-center justify-center">{actionItems.total_items}</span>}
                <ChevronDown className="w-3 h-3 text-slate-500" />
              </div>
            </button>
          ) : (
          <div className="mb-4 rounded-2xl border border-ug-border bg-ug-raised overflow-hidden animate-stagger-1 shadow-ug-sm" data-testid="morning-brief">
            <div className="flex items-center justify-between px-5 py-3 border-b border-ug-border-subtle">
              <div className="flex items-center gap-3">
                {/* A client's brief opens on the website's gem mark (the site look) instead of the diamond glyph. The
                    medallion box stays exactly as it was, border and all (site-look.css only hides its ring): on a
                    phone this row is squeezed, and the border decides how much of that the medallion takes. */}
                <div className="w-8 h-8 rounded-full bg-ug-gold/10 border border-ug-gold/20 flex items-center justify-center">
                  {user.role === 'client'
                    ? <img src="/gem-mark-64.png" srcSet="/gem-mark-64.png 2x, /gem-mark-96.png 3x" alt="" className="w-full h-full object-contain" />
                    : <span className="text-ug-gold text-sm">&#9830;</span>}
                </div>
                <div>
                  <span className="text-sm text-zinc-100 font-sans font-semibold">Welcome to the Editing Cave</span>
                  {actionItems.total_items > 0 && <span className="bg-ug-status-red text-white text-[9px] font-sans font-bold min-w-[20px] h-5 px-1.5 rounded-full inline-flex items-center justify-center shadow-[0_0_8px_rgba(196,114,114,0.3)] ml-2 align-middle">{actionItems.total_items}</span>}
                  <p className="text-[11px] text-zinc-500 italic mt-0.5">{caveGreeting}</p>
                </div>
              </div>
              <button onClick={() => setBriefCollapsed(true)} className="group font-sans text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors"><span className="px-2.5 py-1 rounded-full group-hover:bg-white/[0.05] transition-colors">Collapse</span></button>
            </div>
            {/* Auto-fill: as many tiles per row as fit at 140px each (12 across on a wide screen,
                two on a phone) instead of a fixed five that left half of each tile empty (operator, 2026-09-11). */}
            <div className="grid gap-3 p-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }} data-testid="dashboard-stats" data-filtering={briefFilterKey ? 'true' : undefined}>
              {(() => {
                const sections = user.role === 'editor' ? [
                  { key: 'due_tomorrow', label: 'Due Tomorrow', items: actionItems.due_tomorrow, color: 'text-ug-gold' },
                  { key: 'overdue', label: 'Overdue', items: actionItems.overdue, color: 'text-ug-status-red' },
                  { key: 'new_assignments', label: 'New Assignments', items: actionItems.new_assignments, color: 'text-oxidized-teal' },
                  { key: 'awaiting_feedback', label: 'Awaiting Client Feedback', items: actionItems.awaiting_feedback, color: 'text-oxidized-teal' },
                  { key: 'revision_requests', label: 'Revisions Requested', items: actionItems.revision_requests, color: 'text-ug-status-red' },
                  { key: 'unanswered_admin', label: 'Unread Messages', items: actionItems.unanswered_admin, color: 'text-ug-gold' },
                  { key: 'pending_admin', label: 'Under Admin Review', items: actionItems.pending_admin, color: 'text-ug-gold' },
                ] : user.role === 'client' ? [
                  { key: 'drafts_ready', label: 'Ready for Review', items: actionItems.drafts_ready, color: 'text-ug-gold' },
                  { key: 'unanswered', label: 'Unread Messages', items: actionItems.unanswered, color: 'text-ug-status-red' },
                  ...(actionItems.style_incomplete ? [{ key: 'style', label: 'Complete Profile', items: [{ id: 'style' }], color: 'text-ug-gold' }] : []),
                ] : [
                /* Mentions leads: a tag is someone waiting on YOU by name. Fed by
                   the same chat_reads cursor as the chat cards, so opening the
                   chat clears this card and those together — one read state,
                   never two lists to reconcile. */
                /* The only tile on this row a PERSON fills in. Everything else is a condition the
                   system spotted; this is the jobs somebody tagged because no rule was ever going
                   to notice them. Sorted with the red cards for that reason, and deliberately NOT
                   counted in the sidebar badge (see routes/analytics.py): a badge you can only
                   clear by untagging a job is a badge people learn to ignore. */
                { key: 'needs_attention', label: 'Needs Attention', items: actionItems.needs_attention, color: 'text-ug-status-red', bg: 'bg-ug-status-red/10' },
                { key: 'mentions', label: 'Mentions', items: actionItems.mentions, color: 'text-ug-status-red', bg: 'bg-ug-status-red/10' },
                { key: 'revision_requests', label: 'Revisions Requested', items: actionItems.revision_requests, color: 'text-ug-status-red', bg: 'bg-ug-status-red/10' },
                { key: 'pending_review', label: 'Pending Review', items: actionItems.pending_review, color: 'text-ug-gold', bg: 'bg-ug-gold/10' },
                { key: 'unanswered_client', label: 'Client Chats', items: actionItems.unanswered_client, color: 'text-ug-status-red', bg: 'bg-ug-status-red/10' },
                { key: 'unanswered_editor', label: 'Editor Chats', items: actionItems.unanswered_editor, color: 'text-ug-status-red', bg: 'bg-ug-status-red/10' },
                { key: 'overdue', label: 'Overdue', items: actionItems.overdue, color: 'text-ug-status-red', bg: 'bg-ug-status-red/10' },
                /* Both file-request escalations land here — cannot_provide and 48h
                   stale — ranked server-side by needs_attention(). Red because both
                   mean somebody is stuck waiting on us.

                   POINTER, NOT A CONTROL. Clicking filters the list like every other
                   card; resolving and cancelling live in the drawer, where an admin
                   can see the whole exchange before deciding. Two places that change
                   state is how the two diverge.

                   Label matches the dashboard chip on purpose: "Files Requested" is
                   the same words the client and editor see for the same thing. */
                { key: 'file_requests', label: 'Files Requested', items: actionItems.file_requests, color: 'text-ug-status-red', bg: 'bg-ug-status-red/10' },
                { key: 'no_editor', label: 'No Editor', items: actionItems.no_editor, color: 'text-ug-gold', bg: 'bg-ug-gold/10' },
                /* Paid, still at Project Requested, has a link, and the checker never reached a
                   verdict. Auto-advance never moves an unchecked link, so these wait for a person. */
                { key: 'unverified_links', label: 'Link Never Verified', items: actionItems.unverified_links, color: 'text-ug-gold', bg: 'bg-ug-gold/10' },
                /* 'New Orders' tile dropped from the brief (operator, 2026-09-11): not needed. The filter key
                   stays valid for deep links; the count itself is no longer surfaced on the board. */
                { key: 'due_tomorrow', label: 'Due Tomorrow', items: actionItems.due_tomorrow, color: 'text-ug-gold', bg: 'bg-ug-gold/10' },
                { key: 'not_started', label: 'Not Started', items: actionItems.not_started, color: 'text-ug-gold', bg: 'bg-ug-gold/10' },
                { key: 'awaiting_client', label: 'Awaiting Client', items: actionItems.awaiting_client, color: 'text-oxidized-teal', bg: 'bg-oxidized-teal/10' },
                { key: 'recently_approved', label: 'Needs Follow-Up', items: actionItems.recently_approved, color: 'text-oxidized-teal' },
              ];
              // Display-only: hide cards with no action (count 0); reflow the rest.
              // All-clear is driven by the VISIBLE-card count, NOT total_items
              // (which only sums 5 of the cards and would falsely read "all clear").
              // Colour decides the order (operator, 2026-09-11): red first, gold second, teal third,
              // stable within a colour so the usual order survives inside each band.
              const urgency = (c) => (c || '').includes('status-red') ? 0 : (c || '').includes('ug-gold') ? 1 : 2;
              const visible = sections.filter(s => s.items?.length > 0).sort((x, y) => urgency(x.color) - urgency(y.color));
              if (visible.length === 0) {
                return (
                  <div className="col-span-full flex items-center justify-center gap-2 py-6" data-testid="brief-all-clear">
                    <span className="font-sans text-[11px] font-medium text-oxidized-teal">&#10003; All clear — nothing needs your attention</span>
                  </div>
                );
              }
              return visible.map(s => (
                <button key={s.key} data-testid={`brief-card-${s.key}`} onClick={() => {
                    if (s.key === 'style') { navigate('/style-profile'); return; }
                    toggleBriefFilter(s.key);
                  }}
                  data-active={briefFilterKey === s.key ? 'true' : undefined}
                  aria-pressed={briefFilterKey === s.key}
                  /* Selected look lives in index.css (data-active): a Tailwind ring is a
                     box-shadow, and the light-mode tile rule sets box-shadow !important, so
                     the old ring never showed in light and was faint in dark. */
                  className={`ug-lift p-3 text-center rounded-2xl transition-all flex flex-col justify-start min-h-[72px] cursor-pointer ${briefFilterKey === s.key ? 'bg-ug-surface' : 'bg-ug-raised hover:bg-ug-surface/50'}`}>
                  <span className={`font-sans text-lg font-bold ${s.color}`}>{s.items?.length || '—'}</span>
                  <span className="block font-sans text-[8px] font-medium uppercase tracking-wider text-zinc-500 mt-1 leading-tight">{s.label}</span>
                </button>
              ));
              })()}
            </div>
          </div>
          )
        )}


        {/* Client Shooting Tip of the Day */}
        {user.role === 'client' && (
          <ClientTipOfDay />
        )}

        {/* Editor Editing Tip of the Day */}
        {user.role === 'editor' && (
          <EditorTipOfDay />
        )}

        {/* Urgent revision banner for editors */}
        {user.role === 'editor' && (() => {
          const revisionProjects = projects.filter(p => p.revisions_requested && p.editor_status_id !== 4);
          if (revisionProjects.length === 0) return null;
          // A clock an admin or PM paused (backend app/revision_clock.py) is not urgent: said apart, and calmly.
          const urgent = revisionProjects.filter(p => !p.revision_paused);
          const paused = revisionProjects.filter(p => p.revision_paused);
          return (
            <>
              {urgent.length > 0 && (
                <div className="mb-4 p-4 rounded-lg bg-ug-status-red/[0.08] border-2 border-[#F97066]/40 animate-pulse-slow">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-ug-status-red font-sans text-[11px] font-medium uppercase tracking-wider font-bold">Urgent: Revision{urgent.length > 1 ? 's' : ''} Requested</span>
                  </div>
                  <p className="text-[11px] text-ug-status-red/80">
                    {urgent.map(p => p.title).join(', ')} — Revisions must be completed before continuing other work. Please address immediately.
                  </p>
                </div>
              )}
              {paused.length > 0 && (
                <div className="mb-4 p-3 rounded-lg bg-oxidized-teal/[0.06] border border-oxidized-teal/30 space-y-1" data-testid="revision-paused-banner">
                  {paused.map(p => (
                    <p key={p.id} className="text-[11px] text-oxidized-teal" data-testid={`revision-paused-banner-${p.id}`}>
                      Revision clock paused on {p.title}{p.revision_paused.by_name ? ` by ${p.revision_paused.by_name}` : ''}: {p.revision_paused.reason}.
                      {' '}Paused time does not count against your revision rate; the deadline moves later by it.
                    </p>
                  ))}
                </div>
              )}
            </>
          );
        })()}

        {/* Search + Type Filter */}
        <div className="mb-3 animate-stagger-2 flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            {/* pr-9 reserves room for the clear button so a long query cannot run
                underneath it. */}
            <Input data-testid="search-projects" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects..." className="bg-ug-raised/40 border-ug-border text-zinc-100 pl-10 pr-9 h-9 text-sm rounded-full" />
            {search && (
              <button type="button" onClick={() => setSearch('')} aria-label="Clear search"
                data-testid="search-projects-clear"
                className="tap-auto absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded text-slate-500 hover:text-zinc-200 hover:bg-white/5 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {/* Include archived — only on the main board. The Archived page and Trash
              are already single-list views where a widening toggle means nothing. */}
          {!archived && !trashed && (
            <button type="button" onClick={() => setIncludeArchived(v => !v)}
              data-testid="include-archived-toggle"
              title={includeArchived
                ? 'Currently searching active AND archived projects. Click to show active only.'
                : 'Currently showing active projects only. Click to include archived ones.'}
              className={`tap-auto ug-lift flex items-center gap-1.5 h-9 px-3 rounded-full border text-[10px] font-sans font-semibold uppercase tracking-wider whitespace-nowrap transition-colors ${
                includeArchived
                  ? 'border-ug-gold/40 bg-ug-gold/10 text-ug-gold'
                  : 'border-ug-border text-slate-500 hover:text-ug-gold hover:border-gold-leaf/30'}`}>
              <Archive className="w-3.5 h-3.5 shrink-0" />
              {includeArchived ? 'Incl. archived' : 'Active only'}
            </button>
          )}
          {isAdmin && (
            <Select value={publicityFilter} onValueChange={setPublicityFilter}>
              <SelectTrigger data-testid="publicity-filter"
                className="tap-auto w-[130px] h-9 bg-ug-raised/40 border-ug-border text-zinc-300 text-[10px] font-sans">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end" className="bg-ug-raised border-ug-border-strong">
                <SelectItem value="all" className="text-zinc-100 text-[11px]">All Publicity</SelectItem>
                <SelectItem value="yes" className="text-zinc-100 text-[11px]">Shareable</SelectItem>
                <SelectItem value="no" className="text-zinc-100 text-[11px]">Private</SelectItem>
                <SelectItem value="ask_first" className="text-zinc-100 text-[11px]">Ask First</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* The confusion this feature exists to prevent: you search a past client,
            get nothing, and conclude they never ordered — when in fact every job
            they have is archived. Says so, and includes them in one click. */}
        {archivedMatchCount > 0 && (
          <div className="mb-3 -mt-1 flex items-center gap-2 text-[11px] text-slate-400"
            data-testid="archived-match-hint">
            <Archive className="w-3.5 h-3.5 text-ug-gold/60 shrink-0" />
            <span>
              {archivedMatchCount} archived project{archivedMatchCount === 1 ? '' : 's'} also
              match{archivedMatchCount === 1 ? 'es' : ''} &ldquo;{search}&rdquo;.
            </span>
            <button type="button" onClick={() => setIncludeArchived(true)}
              data-testid="archived-match-hint-include"
              className="tap-auto text-ug-gold hover:underline font-semibold">
              Include them
            </button>
          </div>
        )}

        {/* Status filter pills — admin/staff only, view filter (no data changes) */}
        {isAdmin && (() => {
          const nonEmpty = groups.filter(g => g.items.length > 0);
          if (nonEmpty.length === 0) return null;
          const totalCount = nonEmpty.reduce((n, g) => n + g.items.length, 0);
          const clearOrSet = (id) => setStatusPillFilter(prev => (prev === id ? null : id));
          return (
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="status-pill-row">
              <button
                type="button"
                onClick={() => setStatusPillFilter(null)}
                data-testid="status-pill-all"
                data-active={statusPillFilter === null}
                /* Same recipe as the column-header strip below the row: a capsule tinted from
                   --grp, no border, 9px mono uppercase. Colour and state live in index.css
                   (.status-chip); the row and the strips speak one language. */
                className="tap-auto status-chip inline-flex items-center gap-1.5 h-[26px] px-3 rounded-full font-mono text-[9px] uppercase tracking-wider"
                style={{ '--grp': '#c6a96f' }}
              >
                All
                <span className="status-chip-count font-mono text-[9px] font-normal">{totalCount}</span>
              </button>
              {nonEmpty.map(group => {
                const active = statusPillFilter === group.id;
                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => clearOrSet(group.id)}
                    data-testid={`status-pill-${group.id}`}
                    data-active={active}
                    className="tap-auto status-chip inline-flex items-center gap-1.5 h-[26px] px-3 rounded-full font-mono text-[9px] uppercase tracking-wider"
                    style={{ '--grp': group.color }}
                  >
                    {group.name}
                    <span className="status-chip-count font-mono text-[9px] font-normal">{group.items.length}</span>
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* Sticky Morning Brief filter chip */}
        {briefFilterKey && !archived && !trashed && (
          <div className="sticky top-2 z-30 mb-4 tap-auto" data-testid="brief-filter-chip">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1c1a14] border border-ug-gold/40 shadow-[0_4px_24px_rgba(0,0,0,0.45)] backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-ug-gold animate-pulse shrink-0" />
              <span className="tap-auto flex items-center font-sans text-[11px] font-semibold text-ug-gold truncate" data-testid="brief-filter-chip-text">
                Showing {filtered.length} of {projects.length} · {BRIEF_CARD_LABELS[briefFilterKey] || briefFilterKey}
              </span>
              {briefFilterKey === 'no_editor' && noEditorPinnedIds.size > 0 && (
                <span data-testid="no-editor-in-progress-badge"
                  className="tap-auto shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-sans font-semibold text-ug-gold/80 bg-ug-gold/10 border border-ug-gold/20"
                  title="Rows assigned to an editor this session but still at Files Processed. They clear when status moves to Assigned to Editor.">
                  · +{noEditorPinnedIds.size} in progress
                </span>
              )}
              <button onClick={clearBriefFilter} data-testid="brief-filter-clear"
                className="tap-auto ml-auto shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-sans font-semibold text-obsidian bg-ug-gold hover:bg-ug-gold-bright transition-colors">
                <X className="w-3 h-3" /> Clear
              </button>
            </div>
          </div>
        )}

        {/* Board / List */}
        {loading ? (
          <div className="space-y-4 animate-pulse py-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <div className="w-4 h-4 rounded bg-white/[0.04]" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-white/[0.06] rounded w-1/3" />
                  <div className="h-2 bg-white/[0.03] rounded w-2/3" />
                </div>
                <div className="h-5 bg-white/[0.04] rounded-full w-20" />
              </div>
            ))}
          </div>
        ) : trashed ? (
          /* ═══ TRASH: Flat list with restore + days remaining ═══ */
          <div className="space-y-2 animate-stagger-3" data-testid="trash-list">
            {filtered.length === 0 && <p className="text-center py-12 font-sans text-xs font-medium text-slate-600">Trash is empty</p>}
            {filtered.map(project => {
              const daysLeft = project.deleted_at ? Math.max(0, 30 - Math.floor((Date.now() - new Date(project.deleted_at).getTime()) / 86400000)) : 30;
              return (
                <div key={project.id} data-testid={`trash-row-${project.id}`}
                  className="p-4 rounded-md border border-ug-border-subtle hover:bg-ug-surface/50 transition-colors">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <span className="text-[14px] text-zinc-100 font-medium">{project.title}</span>
                    <span className="font-sans text-[9px] font-semibold text-velvet-burgundy">{daysLeft} day{daysLeft !== 1 ? 's' : ''} until permanent deletion</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] font-mono text-slate-500">
                    <span>{project.brand_name || project.studio_name || project.client_name}</span>
                    {project.event_date && <span>{project.event_date}</span>}
                    {project.deleted_at && <span className="text-slate-600">Deleted {new Date(project.deleted_at).toLocaleDateString()}</span>}
                    <div className="flex gap-2 ml-auto">
                      {canRestoreFromTrash(user) && (
                        <button onClick={() => { api.put(`/projects/${project.id}/restore`).then(() => { toast.success('Project restored'); fetchProjects(); }).catch(() => toast.error('Failed')); }}
                          data-testid={`restore-${project.id}`}
                          className="text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 font-mono uppercase tracking-wider tap-sm transition-colors">
                          Restore
                        </button>
                      )}
                      {user.role === 'client' && (
                        <span data-testid={`trash-restore-help-${project.id}`} className="tap-auto font-sans text-[11px] text-slate-500">
                          {TRASH_HELP_LEAD}{' '}
                          <a href={`mailto:${TRASH_HELP_EMAIL}`} className="text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2">{TRASH_HELP_EMAIL}</a>.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : archived ? (
          /* ═══ ARCHIVED: Flat list, no groups ═══ */
          <div className="space-y-2 animate-stagger-3" data-testid="archived-list">
            {filtered.length === 0 && <p className="text-center py-12 font-sans text-xs font-medium text-slate-600">No archived projects</p>}
            {filtered.map(project => (
              <div key={project.id} data-testid={`archived-row-${project.id}`} onClick={() => setSelectedProject(project)}
                className={`p-4 rounded-md cursor-pointer transition-all border
                  ${selectedProject?.id === project.id ? 'bg-ug-gold/[0.06] border-ug-gold/15' : 'border-ug-border-subtle hover:bg-ug-surface/60'}`}>
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <div className="flex-1 min-w-0">
                    <span className="text-[14px] text-zinc-100 font-medium">{project.title}</span>
                    {project.current_version > 0 && <span className="font-sans text-[10px] font-semibold text-oxidized-teal ml-2">V{project.current_version}</span>}
                  </div>
                  <Badge className="font-sans text-[9px] font-semibold uppercase tracking-wider border-0 px-2 py-0.5 bg-[#2A6B6A] text-white shrink-0">Approved</Badge>
                </div>
                <div className="flex items-center gap-4 text-[11px] font-mono text-slate-500">
                  <span>{project.brand_name || project.studio_name || project.client_name}</span>
                  {project.event_date && <span>{project.event_date}</span>}
                  {project.my_archived_at && <span className="text-slate-600">Archived {new Date(project.my_archived_at).toLocaleDateString()}</span>}
                  {(project.products || []).slice(0, 2).map((p, i) => <span key={i} className="text-[9px] bg-white/[0.04] px-1.5 py-0.5 rounded">{sanitizeProductLabel(p, user.role)}</span>)}
                  {/* Every role: the route un-archives the CALLER'S side only. */}
                  {(
                    <button onClick={(e) => { e.stopPropagation(); api.put(`/projects/${project.id}/unarchive`).then(() => { toast.success('Project un-archived'); fetchProjects(); }).catch(() => toast.error('Failed')); }}
                      className="ml-auto text-[9px] text-ug-gold/60 hover:text-ug-gold transition-colors tap-sm" data-testid={`unarchive-${project.id}`}>
                      Un-archive
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* ═══ ACTIVE: Grouped board ═══ */
          <div>
            {/* Bulk action bar — floating pill, fixed bottom-center so it follows the scroll */}
            {isAdmin && selectedIds.size > 0 && (
              <div data-testid="bulk-action-bar"
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-ug-raised/95 backdrop-blur-sm border border-ug-gold/25 shadow-2xl shadow-black/50">
                <span className="font-sans text-[10px] font-semibold text-ug-gold whitespace-nowrap">{selectedIds.size} selected</span>
                <Select onValueChange={bulkStatus}>
                  <SelectTrigger className="h-7 w-40 bg-ug-surface border-ug-border-strong text-[10px] font-mono"><SelectValue placeholder="Change status..." /></SelectTrigger>
                  <SelectContent className="bg-ug-raised border-ug-border-strong">{Object.entries(ADMIN_STATUSES).map(([id, name]) => <SelectItem key={id} value={id} className="text-zinc-100 text-xs">{name}</SelectItem>)}</SelectContent>
                </Select>
                <Select onValueChange={async v => { const ids = [...selectedIds]; try { await Promise.all(ids.map(id => api.put(`/projects/${id}/assign-editor`, { editor_id: v }))); toast.success(`Editor assigned to ${ids.length} projects`); setSelectedIds(new Set()); fetchProjects(); refreshWorkload(); } catch { toast.error('Failed'); } }}>
                  <SelectTrigger className="h-7 w-40 bg-ug-surface border-ug-border-strong text-[10px] font-mono"><SelectValue placeholder="Assign editor..." /></SelectTrigger>
                  {/* Bulk assign is the highest-stakes picker — one choice lands on
                      every selected project — so availability belongs here too. */}
                  <SelectContent className="bg-ug-raised border-ug-border-strong">{editors.filter(e => !e.restricted).map(e => (
                    <SelectItem key={e.id} value={e.id} className="text-zinc-100 text-xs">
                      {e.name}{e.out_until && <span className="ml-1.5 text-[9px] font-mono text-orange-400">· back {fmtOutDate(e.out_until)}</span>}
                    </SelectItem>
                  ))}</SelectContent>
                </Select>
                {/* Bulk archive — respects the backend approved-only gate; non-approved are skipped and reported */}
                {archiveConfirm ? (
                  <div className="flex items-center gap-1.5">
                    <span className="font-sans text-[10px] font-semibold text-zinc-300 whitespace-nowrap">Archive {selectedIds.size}?</span>
                    <button data-testid="bulk-archive-confirm" onClick={async () => {
                      const ids = [...selectedIds];
                      try {
                        const results = await Promise.allSettled(ids.map(id => api.put(`/projects/${id}/archive`)));
                        const ok = results.filter(r => r.status === 'fulfilled').length;
                        const skipped = ids.length - ok;
                        if (ok === 0) toast.error('Nothing archived, only approved projects can be archived');
                        else if (skipped > 0) toast.success(`Archived ${ok}, skipped ${skipped} (only approved projects can be archived)`);
                        else toast.success(`Archived ${ok} project${ok !== 1 ? 's' : ''}`);
                      } catch { toast.error('Failed to archive'); }
                      setArchiveConfirm(false); setSelectedIds(new Set()); fetchProjects();
                    }} className="tap-auto h-7 px-3 rounded-md bg-ug-gold/15 border border-ug-gold/40 text-[10px] font-mono font-semibold text-ug-gold hover:bg-ug-gold/25 transition-colors">Yes</button>
                    <button data-testid="bulk-archive-cancel" onClick={() => setArchiveConfirm(false)} className="tap-auto h-7 px-2.5 rounded-md border border-ug-border-strong text-[10px] font-mono text-slate-400 hover:text-slate-200 transition-colors">No</button>
                  </div>
                ) : (
                  <button data-testid="bulk-archive-btn" onClick={() => setArchiveConfirm(true)}
                    className="tap-auto flex items-center gap-1.5 h-7 px-3 rounded-md border border-ug-border-strong bg-ug-surface text-[10px] font-mono text-zinc-200 hover:border-ug-gold/40 hover:text-ug-gold transition-colors">
                    <Archive className="w-3.5 h-3.5" strokeWidth={1.75} /> Archive
                  </button>
                )}
                <button data-testid="bulk-clear-btn" onClick={() => { setSelectedIds(new Set()); setArchiveConfirm(false); }} className="tap-auto font-sans text-[9px] font-semibold text-slate-500 hover:text-slate-300 ml-1">Clear</button>
              </div>
            )}
            {/* Extra bottom padding while the floating bulk bar is up, so the last row can scroll clear of it */}
            <div className={`space-y-4 ${isAdmin && selectedIds.size > 0 ? 'pb-24' : ''}`} data-testid="grouped-board">
              {briefFilterKey && filtered.length === 0 && (
                <div className="text-center py-16" data-testid="brief-filter-empty-state">
                  <p className="font-sans text-sm text-zinc-500 mb-3">No projects currently match "{BRIEF_CARD_LABELS[briefFilterKey] || briefFilterKey}" — the brief may have just updated.</p>
                  <button onClick={clearBriefFilter} data-testid="brief-filter-empty-clear"
                    className="text-[11px] font-sans font-semibold text-ug-gold hover:text-ug-gold-bright underline underline-offset-2">Clear filter</button>
                </div>
              )}
              {filtered.length === 0 && user.role === 'client' && !search && !briefFilterKey && (
                <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="client-empty-state">
                  <div className="w-16 h-16 rounded-2xl bg-ug-gold/[0.08] border border-ug-gold/15 flex items-center justify-center mb-5">
                    <img src="/gem-mark-64.png" srcSet="/gem-mark-64.png 2x, /gem-mark-96.png 3x" alt="" className="w-8 h-8" />
                  </div>
                  <h2 className="font-heading text-xl text-zinc-100 mb-2">Welcome to Uncut Gems</h2>
                  <p className="font-sans text-sm text-zinc-400 mb-6 max-w-sm">Place your first order and we'll take care of the rest. Your projects will appear here once you get started.</p>
                  <Button onClick={() => navigate('/new-order')} className="bg-ug-gold text-obsidian hover:bg-ug-gold/80 font-sans font-semibold text-sm h-10 px-6 rounded-xl" data-testid="empty-state-new-order">
                    Place Your First Order
                  </Button>
                </div>
              )}
              {filtered.length === 0 && search && !briefFilterKey && (
                <div className="text-center py-16" data-testid="empty-search-state">
                  <p className="font-sans text-sm text-zinc-500">No projects matching "{search}"</p>
                </div>
              )}
              {filtered.length === 0 && user.role !== 'client' && !search && !briefFilterKey && (
                <div className="text-center py-16" data-testid="empty-filter-state">
                  <p className="font-sans text-sm text-zinc-500">No projects to display</p>
                </div>
              )}
              {groups.map(group => {
                if (statusPillFilter !== null && group.id !== statusPillFilter) return null;
                if (briefFilterKey && group.items.length === 0) return null;
                const isCollapsed = collapsed[group.id];
                const hasItems = group.items.length > 0;
                return (
                  // min-w-max: the row columns are wider than this container, and the
                  // page already scrolls horizontally. Without it the wrapper stays at
                  // container width, so the group-header hover tint and the coloured
                  // column-header strip painted only part-way across and cut off
                  // mid-table. Sizing the wrapper to its widest child makes every
                  // background span the full row.
                  <div key={group.id} className="min-w-max">
                    <Collapsible open={!isCollapsed} onOpenChange={() => toggleGroup(group.id)}>
                      {/* Group Header */}
                      <CollapsibleTrigger asChild>
                        <button data-testid={`group-header-${group.id}`}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-ug-surface/50 transition-colors select-none">
                          {/* --grp as well: light's avatar rule greys anything round with an inline background, and the
                              client site look puts the status colour back from it (site-look.css) */}
                          <div className="w-[3px] h-5 rounded-full shrink-0" style={{ backgroundColor: group.color, '--grp': group.color }} />
                          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-600" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-600" />}
                          <span className="font-sans text-[11px] font-medium uppercase tracking-wider font-medium" style={{ color: statusInk(group.color, isLight) }}>{group.name}</span>
                          <span className="font-sans text-[10px] font-semibold text-slate-600">{group.items.length} project{group.items.length !== 1 ? 's' : ''}</span>
                        </button>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        {hasItems && (
                          <div>
                            {/* Column headers per group — tinted background matching status color */}
                            <div className="col-header-strip hidden lg:flex items-center gap-0 pr-2 py-1.5 text-[9px] font-mono uppercase tracking-wider text-slate-500 select-none mb-1 mx-1 rounded-full"
                              style={{ '--grp': group.color }}>
                              {isAdmin && <div className="w-[22px] shrink-0" />}
                              {columns.map((col, idx) => (
                                <DraggableColHeader key={col.key} col={col} idx={idx} columns={columns} setWidth={setWidth} reorder={reorder} sortCol={sortCol} sortDir={sortDir} onSort={(key) => { if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol(key); setSortDir('asc'); } }} />
                              ))}
                              {isAdmin && briefFilterKey === 'awaiting_client' && (
                                <button data-testid="waiting-col-header"
                                  onClick={() => { if (sortCol === 'waiting') setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol('waiting'); setSortDir('desc'); } }}
                                  className="tap-auto ml-auto shrink-0 w-[60px] pr-1 text-right font-mono text-[9px] uppercase tracking-wider text-slate-500 hover:text-slate-300 cursor-pointer bg-transparent border-0">
                                  Waiting{sortCol === 'waiting' ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                                </button>
                              )}
                            </div>
                            {group.items.map(project => (
                              <ProjectRowItem key={project.id} project={project} isSelected={selectedProject?.id === project.id} isChecked={selectedIds.has(project.id)}
                                user={user} isAdmin={isAdmin} columns={columns} cfg={cfg} archived={archived} briefFilterKey={briefFilterKey} editors={editors} pms={pms}
                                editorPaymentMap={editorPaymentMap} editorWorkload={editorWorkload} editorRecs={editorRecs} awaitingDaysById={awaitingDaysById}
                                unpaidProjectIds={unpaidProjectIds} uploadingProjectId={uploadingProjectId} selectionAnchorRef={selectionAnchorRef}
                                toggleSelection={toggleSelection} openProject={openProject} setSelectedProject={setSelectedProject} setProjects={setProjects}
                                fetchProjects={fetchProjects} bumpWorkload={bumpWorkload} handleStatusChange={handleStatusChange} handleSetDueDate={handleSetDueDate}
                                handleNudgeEditor={handleNudgeEditor} handleNudgeClient={handleNudgeClient} handleSnoozeOverdue={handleSnoozeOverdue}
                                handleDismissFollowUp={handleDismissFollowUp} tick={tick} />
                            ))}
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>

            {/* Drag overlay */}
            <DragOverlay>
              {activeProject && (
                <div className="bg-ug-raised/95 border border-ug-gold/20 rounded-md px-4 py-2.5 shadow-xl backdrop-blur-sm max-w-sm">
                  <div className="text-[13px] text-zinc-100 font-medium">{activeProject.title}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{activeProject.client_name}</div>
                </div>
              )}
            </DragOverlay>
          </div>
        )}
      </div>

      {/* Right: Project Detail Panel — overlays at all sizes, side panel only on xl+ */}
      {selectedProject && (
        <ProjectPanel project={selectedProject} onClose={() => { setSelectedProject(null); setUploadingProjectId(null); setPanelDefaultTab(null); setPanelExpandChat(null); fetchActionItems(); if (isAdmin) refreshWorkload(); }} onRefresh={refreshSelected} defaultTab={panelDefaultTab} expandChat={panelExpandChat} onChatRead={fetchActionItems} persistedTab={drawerTab} onTabChange={setDrawerTab} onUploading={(isUp) => setUploadingProjectId(isUp ? selectedProject?.id : null)} />
      )}

      {/* Due-date cascade preview modal (admin/staff only — appears when a date
          edit would affect downstream projects). See handleSetDueDate above. */}
      <CascadePreviewDialog
        open={!!cascadePreview}
        onOpenChange={(o) => { if (!o) handleCascadeCancel(); }}
        preview={cascadePreview}
        projectTitle={pendingDateEdit?.projectTitle}
        submitting={cascadeSubmitting}
        onConfirmCascade={handleCascadeConfirm}
        onConfirmSkip={handleCascadeSkip}
        onCancel={handleCascadeCancel}
        onRetry={handleCascadeRetry}
        onLaterModeChange={handleLaterModeChange}
      />
    </div>
  );
}
